import React, { useState, useEffect } from 'react';
import { getAvailableDataSources, getChildKeys, isArrayAtPath, isLeafAtPath, resolveDataPath, getUniqueValues, DEFAULT_INFRASTRUCTURE_CATEGORIES, EXTRA_AUTO_SOURCES } from '../../../widgets/KeyFindingsAdvancedWidgetHelper';
import { DEFAULT_MANAGED_SERVICE_CATEGORIES } from '../../../widgets/ManagedServiceExperienceWidgetHelper';
import { DEFAULT_SECURITY_CATEGORIES } from '../../../widgets/SecurityScanGridWidgetHelper';
import { DEFAULT_CYBER_SECURITY_CATEGORIES } from '../../../widgets/CyberSecurityGridWidgetHelper';
import { MANDATORY_CATEGORIES, recreateFindingsArray, SCAN_TYPE_MAPPING, extractKeyFindingsSummary } from '../../../widgets/FindingsWidgets';
import { calculateAllReportFindings } from '../../../widgets/AllFindingsHelper';
import SearchableSelect from '../SearchableSelect';

const ContentTab = ({ block, updateConfig, dataContext, scanData, pages, theme, scanGroups = [], allCategories = [] }) => {
    const [openFilterId, setOpenFilterId] = useState(null);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);
    const [selectedThresholdCategory, setSelectedThresholdCategory] = useState(MANDATORY_CATEGORIES[0] || '');

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openFilterId && !event.target.closest('.filter-dropdown-container')) {
                setOpenFilterId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openFilterId]);

    if (!block) return null;

    // Variable Categories and Options
    const variableOptions = {
        'Client': [
            { label: 'Client Name', value: '{{ client.name }}' },
            { label: 'Client Email', value: '{{ client.email }}' },
            { label: 'Client Phone', value: '{{ client.phone_number }}' },
            { label: 'Client Domain', value: '{{ client.domain }}' }
        ],
        'MSP': [
            { label: 'Tenant Name', value: '{{ tenant_name }}' },
            { label: 'User First Name', value: '{{ MSP.user.first_name }}' },
            { label: 'User Last Name', value: '{{ MSP.user.last_name }}' },
            { label: 'Logo', value: '{{ MSP.logo }}' },
            { label: 'Large Logo', value: '{{ MSP.large_logo }}' }
        ],
        'Date Today': [
            { label: 'Medium (Jan 1, 2026)', value: '{{ date_today | medium }}' },
            { label: 'Long (January 1, 2026)', value: '{{ date_today | long }}' },
            { label: 'Short (01/01/26)', value: '{{ date_today | short }}' },
            { label: 'Hyphens (01-01-2026)', value: '{{ date_today | "MM-dd-YYYY" }}' },
            { label: 'Slashes (01/01/2026)', value: '{{ date_today | "MM/dd/YYYY" }}' },
            { label: 'Short Hyphens (01-01-26)', value: '{{ date_today | "MM-dd-YY" }}' }
        ],
        'Scan Date': [
            { label: 'Medium (Jan 1, 2026)', value: '{{ date_of_scan | medium }}' },
            { label: 'Long (January 1, 2026)', value: '{{ date_of_scan | long }}' },
            { label: 'Short (01/01/26)', value: '{{ date_of_scan | short }}' },
            { label: 'Hyphens (01-01-2026)', value: '{{ date_of_scan | "MM-dd-YYYY" }}' },
            { label: 'Slashes (01/01/2026)', value: '{{ date_of_scan | "MM/dd/YYYY" }}' },
            { label: 'Short Hyphens (01-01-26)', value: '{{ date_of_scan | "MM-dd-YY" }}' }
        ],
        'Org Name': [
             { label: 'Insert Organization Name', value: '{{ organization_name }}' }
        ]
    };

    const categories = Object.keys(variableOptions);

    // Component for cascading select
    const VariableSelector = ({ onInsert }) => {
        const [selectedCategory, setSelectedCategory] = React.useState('');
        const [selectedValue, setSelectedValue] = React.useState('');

        const handleCategoryChange = (e) => {
            const cat = e.target.value;
            // Check if this category has only one option (like Org Name)
            // If so, insert immediately
            if (cat && variableOptions[cat].length === 1) {
                onInsert(variableOptions[cat][0].value);
                setSelectedCategory(''); // Reset immediately
                setSelectedValue('');
            } else {
                setSelectedCategory(cat);
                setSelectedValue(''); // Reset value when category changes
            }
        };

        const handleVariableChange = (e) => {
            const val = e.target.value;
            if (val) {
                onInsert(val);
                // Reset selection after insertion
                setSelectedValue('');
                setSelectedCategory('');
            }
        };

        return (
            <div style={{ marginBottom: '8px', display: 'flex', gap: '5px' }}>
                <select
                    className="form-select form-select-sm"
                    style={{ flex: 1, fontSize: '0.8rem' }}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                >
                    <option value="">Insert Variable...</option>
                    {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>

                {selectedCategory && (
                    <select
                        className="form-select form-select-sm"
                        style={{ flex: 1, fontSize: '0.8rem', borderColor: '#0d6efd', color: '#0d6efd' }}
                        value={selectedValue}
                        onChange={handleVariableChange}
                    >
                        <option value="">Select Option...</option>
                        {variableOptions[selectedCategory].map((opt, idx) => (
                            <option key={idx} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                )}
            </div>
        );
    };

    // Helper to insert variable at cursor position
    const handleInsertVariable = (variable, field, currentValue) => {
        if (!variable) return;

        const inputId = `input-${block.id}-${field}`;
        const inputEl = document.getElementById(inputId);
        
        let newValue = currentValue || '';
        
        if (inputEl) {
            const start = inputEl.selectionStart;
            const end = inputEl.selectionEnd;
            newValue = newValue.substring(0, start) + variable + newValue.substring(end);
            
            setTimeout(() => {
                inputEl.focus();
                inputEl.setSelectionRange(start + variable.length, start + variable.length);
            }, 0);
        } else {
            newValue += variable;
        }

        updateConfig(field, newValue);
        
        if (block.type === 'widget' && field === 'title') {
             updateConfig('content', newValue);
        }
    };

    // Helper to render the dropdown
    const renderVariableSelect = (field, currentValue) => (
        <VariableSelector onInsert={(val) => handleInsertVariable(val, field, currentValue)} />
    );

    // Helper to handle title updates for widgets
    const handleWidgetTitleChange = (e) => {
        const newValue = e.target.value;
        updateConfig('title', newValue);
        updateConfig('content', newValue);
    };

    // Get available widgets for dependency selection
    const getAvailableParentWidgets = () => {
        if (!pages || !block) return [];
        
        const allWidgets = [];
        
        // Iterate through all pages to find widgets
        pages.forEach((page, index) => {
            if (page.blocks) {
                page.blocks.forEach(b => {
                    // Include all widgets except self
                    if (b.type === 'widget' && b.id !== block.id) {
                        allWidgets.push({
                            id: b.id,
                            label: `${b.config?.title || b.config?.content || b.widgetType || 'Untitled Widget'} (Page ${index + 1})`
                        });
                    }
                });
            }
        });

        return allWidgets;
    };

    const availableParentWidgets = block.type === 'element' ? getAvailableParentWidgets() : [];

    return (
        <div className="property-group">
            <h4>Content Configuration</h4>

            {/* WIDGETS: Title only, excluding single-finding-status */}
            {block.type === 'widget' && block.widgetType !== 'single-finding-status' && (
                <div className="property-field">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                         <label style={{ marginBottom: 0 }}>Title</label>
                    </div>
                    {renderVariableSelect('title', block.config.title || block.config.content)}
                    <input
                        id={`input-${block.id}-title`}
                        type="text"
                        value={block.config.title || block.config.content || ''}
                        onChange={handleWidgetTitleChange}
                        placeholder="Widget Title"
                    />
                </div>
            )}

            {/* Findings Status Widget specific options */}
            {block.type === 'widget' && block.widgetType === 'findings-status' && (
                <div style={{ marginTop: '10px' }}>
                    <div className="property-field">
                        <label>Style Template</label>
                        <select
                            value={block.config?.styleTemplate || 'classic'}
                            onChange={(e) => updateConfig('styleTemplate', e.target.value)}
                        >
                            <option value="classic">Classic (Scorecard)</option>
                            <option value="flexible">Flexible (Customizable)</option>
                        </select>
                    </div>

                    {block.config?.styleTemplate === 'flexible' && (
                        <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '10px' }}>
                            <div className="property-field">
                                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>Visible Elements</label>
                                <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            checked={block.config?.showIcon !== false}
                                            onChange={(e) => updateConfig('showIcon', e.target.checked)}
                                        />
                                        Show Icon
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            checked={block.config?.showLabel !== false}
                                            onChange={(e) => updateConfig('showLabel', e.target.checked)}
                                        />
                                        Show Label
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            checked={block.config?.showLabelBg !== false}
                                            onChange={(e) => updateConfig('showLabelBg', e.target.checked)}
                                        />
                                        Show Label Bg
                                    </label>
                                </div>
                            </div>

                            <div className="property-field" style={{ marginTop: '12px' }}>
                                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>Indicator Targets (Status Color)</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '5px' }}>
                                    {[
                                        { label: 'Icon', key: 'indicatorIcon' },
                                        { label: 'Label Text', key: 'indicatorLabel' },
                                        { label: 'Label Bg', key: 'indicatorLabelBg' },
                                        { label: 'Item Bg', key: 'indicatorBackground' },
                                        { label: 'Item Border', key: 'indicatorBorder' }
                                    ].map(target => (
                                        <label key={target.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                            <input
                                                type="checkbox"
                                                checked={block.config?.[target.key] || (target.key === 'indicatorLabel' && block.config?.styleTemplate !== 'flexible')}
                                                onChange={(e) => updateConfig(target.key, e.target.checked)}
                                            />
                                            {target.label}
                                        </label>
                                    ))}
                                </div>
                                <small style={{ display: 'block', marginTop: '4px', color: '#64748b', fontSize: '0.7rem' }}>
                                    Select which elements should change color based on the score.
                                </small>
                            </div>
                        </div>
                    )}

                    <div className="property-field" style={{ marginTop: '10px' }}>
                        <label>Layout Style</label>
                        <select
                            value={block.config?.layout || 'grid'}
                            onChange={(e) => updateConfig('layout', e.target.value)}
                        >
                            <option value="grid">Grid (Columns)</option>
                            <option value="row">Horizontal List (Row)</option>
                            <option value="column">Vertical List (Column)</option>
                        </select>
                    </div>

                    {(block.config?.layout === 'grid' || !block.config?.layout) && (
                         <div className="property-field">
                            <label>Grid Columns ({block.config?.gridColumns || 3})</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '12px' }}>1</span>
                                <input 
                                    type="range" min="1" max="6" step="1"
                                    value={block.config?.gridColumns || 3} 
                                    onChange={(e) => updateConfig('gridColumns', parseInt(e.target.value))}
                                    style={{ flex: 1 }}
                                />
                                <span style={{ fontSize: '12px' }}>6</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Table & Chart Widgets: Empty State Message & Legend Position */}
            {block.type === 'widget' && (
                ['table', 'bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter'].includes(block.widgetType) ||
                [
                    'overall-risk-score',
                    'ad-admins-status-pie',
                    'external-severity-pie-chart',
                    'external-status-pie-chart',
                    'external-category-bar-chart',
                    'ms365-users-pie-chart',
                    'ms365-mfa-pie-chart',
                    'ms365-policy-pie-chart',
                    'pii-detection-pie-chart',
                    'admins-password-pie-chart',
                    'accounts-password-pie-chart'
                ].includes(block.widgetType)
            ) && (
                <div style={{ marginTop: '10px' }}>
                    <div className="property-field">
                        <label>Empty State Message</label>
                        <input
                            type="text"
                            value={block.config.emptyStateMessage || ''}
                            onChange={(e) => updateConfig('emptyStateMessage', e.target.value)}
                            placeholder="No data to render"
                        />
                        <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '11px' }}>
                            Message to display when the widget has no data.
                        </small>
                    </div>

                    {block.widgetType !== 'table' && (
                        <>
                            <div className="property-field" style={{ marginTop: '10px' }}>
                                <label>Legend Position</label>
                                <select
                                    value={block.config.legendPosition || 'top'}
                                    onChange={(e) => updateConfig('legendPosition', e.target.value)}
                                >
                                    <option value="top">Top</option>
                                    <option value="bottom">Bottom</option>
                                    <option value="left">Left</option>
                                    <option value="right">Right</option>
                                    <option value="none">Hidden</option>
                                </select>
                                <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '11px' }}>
                                    Placement of the chart legend.
                                </small>
                            </div>

                        </>
                    )}
                </div>
            )}

            {/* Cost Summary Column Visibility */}
            {block.type === 'widget' && block.widgetType?.includes('cost-summary') && (
                <div className="property-field">
                    <label>Visible Columns</label>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: '8px', 
                        marginTop: '4px',
                        padding: '10px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                    }}>
                        {['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %'].map(col => {
                            // Default to all columns being included if property doesn't exist
                            const included = block.config?.includedColumns || ['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %'];
                            const isChecked = included.includes(col);
                            
                            return (
                                <label key={col} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px', 
                                    cursor: 'pointer', 
                                    fontWeight: 'normal',
                                    fontSize: '0.75rem',
                                    color: '#374151',
                                    marginBottom: 0
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                            let next;
                                            if (e.target.checked) {
                                                next = [...included, col];
                                            } else {
                                                next = included.filter(c => c !== col);
                                            }
                                            updateConfig('includedColumns', next);
                                        }}
                                    />
                                    {col}
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* OS Versions specific options (lookout field) */}
            {block.type === 'widget' && (block.widgetType === 'network-os-versions-table' || block.widgetType === 'localhost-os-versions-table' || block.widgetType === 'ad-ous-audit-table') && (
                <div className="property-field">
                    <label>Lookout Field (Aggregation)</label>
                    <select
                        value={block.config.lookoutField || (block.widgetType === 'network-os-versions-table' ? 'Device' : block.widgetType === 'ad-ous-audit-table' ? 'Members' : 'Host')}
                        onChange={(e) => updateConfig('lookoutField', e.target.value)}
                    >
                        {block.widgetType === 'network-os-versions-table' ? (
                            <>
                                <option value="Device">Device (One row per device)</option>
                                <option value="OS Name">OS Name (Aggregated By OS)</option>
                            </>
                        ) : block.widgetType === 'ad-ous-audit-table' ? (
                            <>
                                <option value="OU">OU (One row per OU)</option>
                                <option value="Distinguished Name">Distinguished Name</option>
                                <option value="Members">Members (One row per member)</option>
                            </>
                        ) : (
                            <>
                                <option value="Host">Host (Individual Records)</option>
                                <option value="OS Name">OS Name (Aggregated By OS)</option>
                            </>
                        )}
                    </select>
                    <small>
                        {block.config.lookoutField === 'OS Name' 
                            ? "Currently grouping multiple devices by their Operating System."
                            : block.widgetType === 'ad-ous-audit-table'
                                ? `Currently focusing on ${block.config.lookoutField || 'OU'}.`
                                : "Currently showing each device as a separate row."}
                    </small>
                </div>
            )}

            {/* Key Findings Summary specific options */}
            {block.type === 'widget' && block.widgetType === 'key-findings' && (() => {
                // Determine available scans based on dataContext
                const availableScans = [
                    { label: 'All Scans', value: 'All' },
                    { label: 'Localhost Scan', value: 'Localhost Scan' },
                    { label: 'Network Scan', value: 'Network Scan' },
                    { label: 'AD Scan', value: 'AD Scan' },
                    { label: 'MS365 Scan', value: 'MS365 Scan' },
                    { label: 'PII Scan', value: 'PII Scan' }
                ];


                return (
                    <>
                    <div className="property-field">
                        <label>Scan Type Filter</label>
                        <select
                            value={block.config.scanType || 'All'}
                            onChange={(e) => updateConfig('scanType', e.target.value)}
                        >
                            {availableScans.map(scan => (
                                <option key={scan.value} value={scan.value}>{scan.label}</option>
                            ))}
                        </select>
                        <small>Only show findings from available scan types</small>
                    </div>

                    <div className="property-field" style={{ marginTop: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'normal' }}>
                            <input
                                type="checkbox"
                                checked={block.config.hideNoData || false}
                                onChange={(e) => updateConfig('hideNoData', e.target.checked)}
                            />
                            Hide &quot;No Data&quot; Categories
                        </label>
                        <small>Remove categories with no scan data (shown in gray)</small>
                    </div>

                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                        <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Threshold Note Customization</label>
                        
                        <div className="property-field">
                            <label>Select Category</label>
                            <select 
                                value={selectedThresholdCategory}
                                onChange={(e) => setSelectedThresholdCategory(e.target.value)}
                                style={{ width: '100%' }}
                            >
                                {(() => {
                                    const summaries = dataContext ? extractKeyFindingsSummary(dataContext, block.config?.customThresholdNotes || {}) : [];
                                    const allCats = Array.from(new Set([
                                        ...MANDATORY_CATEGORIES,
                                        ...summaries.map(s => s.category)
                                    ])).sort();
                                    return allCats.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ));
                                })()}
                            </select>
                        </div>

                        {selectedThresholdCategory && (() => {
                            const customThresholdNotes = block.config.customThresholdNotes || {};
                            const catNotes = customThresholdNotes[selectedThresholdCategory] || {};
                            
                            const updateCatNote = (status, val) => {
                                const newCustomNotes = {
                                    ...customThresholdNotes,
                                    [selectedThresholdCategory]: {
                                        ...catNotes,
                                        [status]: val
                                    }
                                };
                                updateConfig('customThresholdNotes', newCustomNotes);
                            };

                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                                    {(() => {
                                        const thresholdVars = [
                                            { label: 'Count', value: '{count}' },
                                            { label: 'Total', value: '{total}' },
                                            { label: 'Percentage', value: '{percent}' }
                                        ];

                                        const ThresholdVarSelector = ({ status }) => (
                                            <select 
                                                className="form-select form-select-sm"
                                                style={{ fontSize: '0.7rem', width: 'auto', alignSelf: 'flex-end', marginBottom: '2px', padding: '2px 4px', height: '24px', cursor: 'pointer' }}
                                                value=""
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val) {
                                                        const currentVal = catNotes[status] || '';
                                                        const textareaId = `textarea-${selectedThresholdCategory}-${status}`;
                                                        const el = document.getElementById(textareaId);
                                                        let newVal = currentVal;
                                                        
                                                        if (el) {
                                                            const start = el.selectionStart;
                                                            const end = el.selectionEnd;
                                                            newVal = currentVal.substring(0, start) + val + currentVal.substring(end);
                                                            updateCatNote(status, newVal);
                                                            setTimeout(() => {
                                                                el.focus();
                                                                el.setSelectionRange(start + val.length, start + val.length);
                                                            }, 0);
                                                        } else {
                                                            updateCatNote(status, currentVal + val);
                                                        }
                                                    }
                                                }}
                                            >
                                                <option value="">+ Variable</option>
                                                {thresholdVars.map(v => (
                                                    <option key={v.value} value={v.value}>{v.label}</option>
                                                ))}
                                            </select>
                                        );

                                        // Lookup threshold definition from dataContext
                                        const thresholds = dataContext?.category_thresholds || {};
                                        const catThreshold = thresholds[selectedThresholdCategory];
                                        const rules = catThreshold?.rules || {};

                                        // Map UI status names to threshold tier names
                                        const statusToTier = {
                                            'Critical': 'Bad',
                                            'Warning': 'Moderate',
                                            'Healthy': 'Healthy'
                                        };

                                        const TemplateNoteSelector = ({ status }) => {
                                            const tier = statusToTier[status];
                                            const tierRules = rules[tier] || {};
                                            const adminTierRules = catThreshold?.default_rules ? catThreshold.default_rules[tier] : null;

                                            let displayNotes = [];
                                            const seenTexts = new Set();
                                            
                                            const addNote = (noteObj, sourceLabel) => {
                                                if (!noteObj || !noteObj.text || seenTexts.has(noteObj.text)) return;
                                                displayNotes.push({ 
                                                    ...noteObj, 
                                                    sourceLabel: sourceLabel + (noteObj.isDefault ? ' Default' : '')
                                                });
                                                seenTexts.add(noteObj.text);
                                            };

                                            // 1. Collect Tenant Admin notes (highest priority)
                                            if (Array.isArray(tierRules.notes)) {
                                                tierRules.notes.forEach(n => addNote(n, 'Custom'));
                                            }

                                            // 2. Collect App Admin notes (inherited)
                                            if (adminTierRules && Array.isArray(adminTierRules.notes)) {
                                                adminTierRules.notes.forEach(n => addNote(n, 'App Admin'));
                                            }

                                            // 3. Fallback for legacy note strings
                                            if (tierRules.note) addNote({ text: tierRules.note }, 'Custom');
                                            if (adminTierRules?.note) addNote({ text: adminTierRules.note }, 'App Admin');

                                            // Only show the selector if there's more than one distinct option (or the default is different from current)
                                            if (displayNotes.length === 0) return null;
                                            if (displayNotes.length === 1 && displayNotes[0].text === (catNotes[status] || '')) return null;

                                            return (
                                                <select
                                                    className="form-select form-select-sm"
                                                    style={{ fontSize: '0.7rem', flex: 1, marginBottom: '2px', padding: '2px 4px', height: '24px', cursor: 'pointer', border: '1px solid #0d6efd', color: '#0d6efd', backgroundColor: '#f0f7ff' }}
                                                    value=""
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val) {
                                                            updateCatNote(status, val);
                                                        }
                                                    }}
                                                >
                                                    <option value="">Select Template...</option>
                                                    {displayNotes.map((n, idx) => (
                                                        <option key={idx} value={n.text}>
                                                            {n.text.length > 50 ? n.text.substring(0, 47) + '...' : n.text} ({n.sourceLabel})
                                                        </option>
                                                    ))}
                                                </select>
                                            );
                                        };

                                        return (
                                            <>
                                            <div className="property-field">
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '8px' }}>
                                                    <label style={{ color: '#dc2626', fontSize: '0.75rem', fontWeight: '600', marginBottom: 0, whiteSpace: 'nowrap' }}>Critical (Red)</label>
                                                    <TemplateNoteSelector status="Critical" />
                                                    <ThresholdVarSelector status="Critical" />
                                                </div>
                                                <textarea 
                                                    id={`textarea-${selectedThresholdCategory}-Critical`}
                                                    value={catNotes.Critical || ''}
                                                    onChange={(e) => updateCatNote('Critical', e.target.value)}
                                                    placeholder="Enter custom note for critical status..."
                                                    rows={2}
                                                    style={{ width: '100%', fontSize: '0.75rem', padding: '4px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                                />
                                            </div>
                                            <div className="property-field">
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '8px' }}>
                                                    <label style={{ color: '#eab308', fontSize: '0.75rem', fontWeight: '600', marginBottom: 0, whiteSpace: 'nowrap' }}>Warning (Yellow)</label>
                                                    <TemplateNoteSelector status="Warning" />
                                                    <ThresholdVarSelector status="Warning" />
                                                </div>
                                                <textarea 
                                                    id={`textarea-${selectedThresholdCategory}-Warning`}
                                                    value={catNotes.Warning || ''}
                                                    onChange={(e) => updateCatNote('Warning', e.target.value)}
                                                    placeholder="Enter custom note for warning status..."
                                                    rows={2}
                                                    style={{ width: '100%', fontSize: '0.75rem', padding: '4px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                                />
                                            </div>
                                            <div className="property-field">
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '8px' }}>
                                                    <label style={{ color: '#16a34a', fontSize: '0.75rem', fontWeight: '600', marginBottom: 0, whiteSpace: 'nowrap' }}>Healthy (Green)</label>
                                                    <TemplateNoteSelector status="Healthy" />
                                                    <ThresholdVarSelector status="Healthy" />
                                                </div>
                                                <textarea 
                                                    id={`textarea-${selectedThresholdCategory}-Healthy`}
                                                    value={catNotes.Healthy || ''}
                                                    onChange={(e) => updateCatNote('Healthy', e.target.value)}
                                                    placeholder="Enter custom note for healthy status..."
                                                    rows={2}
                                                    style={{ width: '100%', fontSize: '0.75rem', padding: '4px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                                />
                                            </div>
                                            </>
                                        );
                                    })()}
                                    <div style={{ fontSize: '0.65rem', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '6px', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                                        <strong>Available Variables:</strong>
                                        <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                                            <li><code>{'{count}'}</code> - Number of items found (e.g. 5 devices)</li>
                                            <li><code>{'{total}'}</code> - Total items scanned</li>
                                            <li><code>{'{percent}'}</code> - Compliance percentage</li>
                                        </ul>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                    </>
                );
            })()}

            {/* Sensitive Open Ports specific options */}
            {block.type === 'widget' && block.widgetType === 'open-ports' && (
                <div className="property-field">
                    <small style={{ color: '#666', fontStyle: 'italic' }}>
                        Configure columns and sorting in the <strong>Data</strong> tab.
                    </small>
                </div>
            )}

            {/* Single Finding Status - Multi-Finding Management */}
            {block.type === 'widget' && block.widgetType === 'single-finding-status' && (() => {
                // Extract findings from dataContext - use the robust helper that combines all sources
                const rawFindings = calculateAllReportFindings(dataContext, scanGroups, allCategories);

                const processedFindings = rawFindings
                    .filter(f => Array.isArray(f) && f.length >= 3)
                    .map(f => {
                        const name = f[0] || '';
                        const scoreStr = f[2];
                        let score = null;
                        if (scoreStr !== null && scoreStr !== undefined) {
                            const scoreNum = typeof scoreStr === 'string'
                                ? parseFloat(scoreStr.replace('%', ''))
                                : parseFloat(scoreStr);
                            score = isNaN(scoreNum) ? null : scoreNum;
                        }
                        const sColors = theme?.statusColors || { green: '#40B657', yellow: '#FCC419', red: '#FF0000' };
                        let color = '#808080';
                        if (score !== null) {
                            if (score < 20) color = sColors.green;
                            else if (score < 50) color = sColors.yellow;
                            else color = sColors.red;
                        }
                        return { name, score, color };
                    });

                // Get all possible categories from any group, ensuring no duplicates
                const existingNames = new Set(processedFindings.map(f => f.name));
                const extraFindings = (allCategories || [])
                    .filter(cat => cat && cat.name && !existingNames.has(cat.name))
                    .map(cat => ({
                        name: cat.name,
                        score: null,
                        color: '#808080'
                    }));

                const combinedFindings = [...processedFindings, ...extraFindings];
                const seenNames = new Set();
                const allPossibleFindings = combinedFindings.filter(f => {
                    if (seenNames.has(f.name)) return false;
                    seenNames.add(f.name);
                    return true;
                });

                // Backward compat: selectedFinding → selectedFindings
                let selectedFindings = block.config?.selectedFindings;
                if (!selectedFindings || !Array.isArray(selectedFindings)) {
                    if (block.config?.selectedFinding) {
                        selectedFindings = [block.config.selectedFinding];
                    } else {
                        selectedFindings = allPossibleFindings.length > 0 ? [allPossibleFindings[0].name] : [];
                    }
                }

                const availableToAdd = allPossibleFindings.filter(f => !selectedFindings.includes(f.name));

                const addFinding = (name) => {
                    const updated = [...selectedFindings, name];
                    updateConfig('selectedFindings', updated);
                };

                const removeFinding = (name) => {
                    const updated = selectedFindings.filter(n => n !== name);
                    updateConfig('selectedFindings', updated);
                };

                return (
                    <>
                    {/* Grid Type Selector */}
                    <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#f0f7ff', borderRadius: '6px', border: '1px solid #cce3ff' }}>
                        <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Grid Type</label>
                        <select
                            value={block.config?.gridType || 'custom'}
                            onChange={(e) => {
                                const newGridType = e.target.value;
                                let newSelected = [];
                                
                                if (newGridType === 'custom') {
                                    newSelected = selectedFindings;
                                } else {
                                    // Map dynamic scan groups
                                    const legacyMap = {
                                        'infrastructure': 'Infrastructure',
                                        'managed-service': 'Managed IT/Service Experience',
                                        'security': 'Security',
                                        'cyber-security': 'Cyber Security (Threat & Exposure)',
                                        'network-scan': 'Network Scan',
                                        'localhost-scan': 'Localhost Scan',
                                        'ad-scan': 'AD Scan',
                                        'ms365-scan': 'MS365 Scan',
                                        'pii-scan': 'PII Scan'
                                    };
                                    const backendKey = legacyMap[newGridType] || newGridType;
                                    newSelected = allCategories
                                        .filter(cat => cat.scan_type === backendKey)
                                        .map(cat => cat.name);
                                }
                                
                                updateConfig('gridType', newGridType);
                                if (newGridType === 'custom') {
                                    updateConfig('selectedFindings', []);
                                } else {
                                    updateConfig('selectedFindings', newSelected);
                                }
                            }}
                            style={{
                                width: '100%', fontSize: '0.75rem', padding: '4px 6px',
                                border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: '#fff'
                            }}
                        >
                            <option value="custom">Custom (Start Blank)</option>
                            {scanGroups.map(group => {
                                const value = (group.key === 'Infrastructure') ? 'infrastructure' :
                                              (group.key === 'Managed IT/Service Experience') ? 'managed-service' :
                                              (group.key === 'Security') ? 'security' :
                                              (group.key === 'Cyber Security (Threat & Exposure)') ? 'cyber-security' :
                                              (group.key === 'Network Scan') ? 'network-scan' :
                                              (group.key === 'Localhost Scan') ? 'localhost-scan' :
                                              (group.key === 'AD Scan') ? 'ad-scan' :
                                              (group.key === 'MS365 Scan') ? 'ms365-scan' :
                                              (group.key === 'PII Scan') ? 'pii-scan' : group.key;
                                return <option key={group.key} value={value}>{group.label}</option>;
                            })}
                        </select>
                    </div>

                    {/* Selected Findings List */}
                    <div className="property-field">
                        <label>Selected Findings ({selectedFindings.length})</label>
                        {selectedFindings.length === 0 && (
                            <small style={{ color: '#999', fontStyle: 'italic' }}>No findings added yet</small>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                            {selectedFindings.map((name, idx) => {
                                const finding = allPossibleFindings.find(f => f.name === name);
                                return (
                                    <div key={idx} style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '4px 8px', backgroundColor: '#f3f4f6', borderRadius: '4px',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                            <span style={{
                                                display: 'inline-block', width: '8px', height: '8px',
                                                borderRadius: '50%', backgroundColor: finding?.color || '#808080'
                                            }} />
                                            {name}
                                        </span>
                                        {(block.config?.gridType === 'custom' || !block.config?.gridType) && (
                                            <button
                                                onClick={() => removeFinding(name)}
                                                style={{
                                                    background: 'none', border: 'none', cursor: 'pointer',
                                                    color: '#9ca3af', fontSize: '16px', padding: '0 4px',
                                                    lineHeight: 1
                                                }}
                                                title="Remove finding"
                                            >✕</button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Add Finding Dropdown - Only available in Custom mode */}
                    {(block.config?.gridType === 'custom' || !block.config?.gridType) && availableToAdd.length > 0 && (
                        <div className="property-field">
                            <label>Add Finding</label>
                            <SearchableSelect
                                value=""
                                placeholder="— Select to add —"
                                onChange={(val) => { if (val) addFinding(val); }}
                                options={availableToAdd.map(f => ({
                                    label: f.name,
                                    value: f.name,
                                    color: f.color
                                }))}
                                renderOption={(opt) => (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                                        <span style={{
                                            display: 'inline-block', width: '8px', height: '8px',
                                            borderRadius: '50%', backgroundColor: opt.color || '#808080'
                                        }} />
                                        {opt.label}
                                    </span>
                                )}
                            />
                        </div>
                    )}

                    {/* Style Template Selector */}
                    <div className="property-field" style={{ marginTop: '15px' }}>
                        <label>Style Template</label>
                        <select
                            value={block.config?.styleTemplate || 'classic'}
                            onChange={(e) => updateConfig('styleTemplate', e.target.value)}
                        >
                            <option value="classic">Classic (Box)</option>
                            <option value="modern">Modern (Underline)</option>
                            <option value="flexible">Flexible (Customizable)</option>
                        </select>
                    </div>

                    {block.config?.styleTemplate === 'flexible' && (
                        <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px' }}>
                            <div className="property-field">
                                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>Visible Elements</label>
                                <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            checked={block.config?.showIcon !== false}
                                            onChange={(e) => updateConfig('showIcon', e.target.checked)}
                                        />
                                        Show Icon
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            checked={block.config?.showLabel !== false}
                                            onChange={(e) => updateConfig('showLabel', e.target.checked)}
                                        />
                                        Show Label
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            checked={block.config?.showLabelBg !== false}
                                            onChange={(e) => updateConfig('showLabelBg', e.target.checked)}
                                        />
                                        Show Label Bg
                                    </label>
                                </div>
                            </div>

                            <div className="property-field" style={{ marginTop: '12px' }}>
                                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>Indicator Targets (Status Color)</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '5px' }}>
                                    {[
                                        { label: 'Icon', key: 'indicatorIcon' },
                                        { label: 'Label Text', key: 'indicatorLabel' },
                                        { label: 'Label Bg', key: 'indicatorLabelBg' },
                                        { label: 'Item Bg', key: 'indicatorBackground' },
                                        { label: 'Item Border', key: 'indicatorBorder' }
                                    ].map(target => (
                                        <label key={target.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                            <input
                                                type="checkbox"
                                                checked={block.config?.[target.key]}
                                                onChange={(e) => updateConfig(target.key, e.target.checked)}
                                            />
                                            {target.label}
                                        </label>
                                    ))}
                                </div>
                                <small style={{ display: 'block', marginTop: '4px', color: '#64748b', fontSize: '0.7rem' }}>
                                    Select which elements should change color based on the score.
                                </small>
                            </div>
                        </div>
                    )}

                    {/* Layout Toggle - For both Classic and Modern */}
                    <div className="property-field">
                        <label>Layout Style</label>
                        <select
                            value={block.config?.layout || 'row'}
                            onChange={(e) => updateConfig('layout', e.target.value)}
                        >
                            <option value="row">Row (Horizontal)</option>
                            <option value="column">Column (Vertical)</option>
                            <option value="grid">Grid</option>
                        </select>
                    </div>

                    {/* Grid Columns (Only visible if layout === 'grid') */}
                    {block.config?.layout === 'grid' && (
                        <div className="property-field">
                            <label>Grid Columns ({block.config?.gridColumns || 3})</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '12px' }}>1</span>
                                <input 
                                    type="range" min="1" max="10" step="1"
                                    value={block.config?.gridColumns || 3} 
                                    onChange={(e) => updateConfig('gridColumns', parseInt(e.target.value))}
                                    style={{ flex: 1 }}
                                />
                                <span style={{ fontSize: '12px' }}>10</span>
                            </div>
                        </div>
                    )}

                    {/* Indicator Target - Only for Classic */}
                    {(block.config?.styleTemplate === 'classic') && (
                        <div className="property-field">
                            <label>Status Color (Classic)</label>
                            <select
                                value={block.config?.indicatorTarget === 'none' ? 'none' : 'text'}
                                onChange={(e) => updateConfig('indicatorTarget', e.target.value)}
                            >
                                <option value="text">Show on Label Background</option>
                                <option value="none">None (Use Global Styles)</option>
                            </select>
                            <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.75rem' }}>
                                Apply green/yellow/red status color to the text label.
                            </small>
                        </div>
                    )}

                    {/* Icon Size Slider */}
                    <div className="property-field">
                        <label>Icon Size ({block.config?.iconSize || 80}%)</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '12px' }}>10%</span>
                            <input 
                                type="range" min="10" max="100"
                                value={block.config?.iconSize || 80} 
                                onChange={(e) => updateConfig('iconSize', parseInt(e.target.value))}
                                style={{ flex: 1 }}
                            />
                            <span style={{ fontSize: '12px' }}>100%</span>
                        </div>
                    </div>
                    </>
                );
            })()}

            {/* Total Cost Calculator Configuration */}
            {block.type === 'widget' && block.widgetType === 'total-cost-calculator' && (
                <>
                    <div className="property-field" style={{ marginTop: '15px' }}>
                        <label>Layout</label>
                        <select
                            value={block.config?.layout || 'column'}
                            onChange={(e) => updateConfig('layout', e.target.value)}
                        >
                            <option value="column">Column (Vertical)</option>
                            <option value="row">Row (Horizontal)</option>
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                        <div className="property-field" style={{ marginTop: 0 }}>
                            <label>Horizontal Alignment</label>
                            <select
                                value={block.config?.horizontalAlign || 'center'}
                                onChange={(e) => updateConfig('horizontalAlign', e.target.value)}
                            >
                                <option value="flex-start">Left (Start)</option>
                                <option value="center">Center</option>
                                <option value="flex-end">Right (End)</option>
                                <option value="space-between">Space Between</option>
                                <option value="space-around">Space Around</option>
                            </select>
                        </div>

                        <div className="property-field" style={{ marginTop: 0 }}>
                            <label>Vertical Alignment</label>
                            <select
                                value={block.config?.verticalAlign || 'center'}
                                onChange={(e) => updateConfig('verticalAlign', e.target.value)}
                            >
                                <option value="flex-start">Top</option>
                                <option value="center">Middle</option>
                                <option value="flex-end">Bottom</option>
                            </select>
                        </div>
                    </div>

                    <div className="property-field" style={{ marginTop: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <label style={{ marginBottom: 0 }}>Note Text</label>
                        </div>
                        {renderVariableSelect('noteText', block.config.noteText || "This is the sum of all finding costs across all security categories.")}
                        <textarea
                            id={`input-${block.id}-noteText`}
                            value={block.config.noteText !== undefined ? block.config.noteText : "This is the sum of all finding costs across all security categories."}
                            onChange={(e) => updateConfig('noteText', e.target.value)}
                            rows={3}
                            placeholder="Explanatory note..."
                            style={{ 
                                width: '100%', 
                                fontSize: '0.75rem', 
                                padding: '4px 6px',
                                border: '1px solid #d1d5db', 
                                borderRadius: '4px', 
                                resize: 'vertical' 
                            }}
                        />
                    </div>

                    <div className="property-field" style={{ marginTop: '15px' }}>
                        <label style={{ marginBottom: '8px', display: 'block' }}>Display Elements</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={block.config?.showTitle !== false}
                                    onChange={(e) => updateConfig('showTitle', e.target.checked)}
                                />
                                Show Title
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={block.config?.showAmount !== false}
                                    onChange={(e) => updateConfig('showAmount', e.target.checked)}
                                />
                                Show Amount
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={block.config?.showNote !== false}
                                    onChange={(e) => updateConfig('showNote', e.target.checked)}
                                />
                                Show Note
                            </label>
                        </div>
                    </div>
                </>
            )}

            {/* Infrastructure Scan Grid, Managed Service Experience, Security Scan Grid, Cyber Security & Combined Scan Grid Configuration */}
            {block.type === 'widget' && (block.widgetType === 'infrastructure-scan-grid' || block.widgetType === 'managed-service-experience' || block.widgetType === 'security-scan-grid' || block.widgetType === 'cyber-security-threat-exposure' || block.widgetType === 'combined-scan-grid' || block.widgetType === 'advanced-scan-grid') && (() => {
                
                let defaultCategories = [];
                const activeGridType = (block.widgetType === 'combined-scan-grid' || block.widgetType === 'advanced-scan-grid') 
                    ? (block.config?.gridType || (block.widgetType === 'advanced-scan-grid' ? 'custom' : 'infrastructure')) 
                    : block.widgetType;

                if (activeGridType === 'infrastructure-scan-grid' || activeGridType === 'infrastructure') {
                    defaultCategories = DEFAULT_INFRASTRUCTURE_CATEGORIES;
                } else if (activeGridType === 'managed-service-experience' || activeGridType === 'managed-service') {
                    defaultCategories = DEFAULT_MANAGED_SERVICE_CATEGORIES;
                } else if (activeGridType === 'security-scan-grid' || activeGridType === 'security') {
                    defaultCategories = DEFAULT_SECURITY_CATEGORIES;
                } else if (activeGridType === 'cyber-security-threat-exposure' || activeGridType === 'cyber-security') {
                    defaultCategories = DEFAULT_CYBER_SECURITY_CATEGORIES;
                }

                const categories = block.config?.categories || defaultCategories;

                const handleAddCategory = () => {
                    const newId = `custom-${Date.now()}`;
                    const newCategories = [
                        ...categories,
                        { id: newId, label: 'New Grid Item' }
                    ];
                    updateConfig('categories', newCategories);
                    // Default to manual mode Green with a friendly description for better UX
                    updateCategoryOverride(newId, { 
                        mode: 'manual', 
                        status: 'Green',
                        description: 'Requirement satisfied or status is healthy'
                    });
                };

                const handleRemoveCategory = (catId, e) => {
                    e.stopPropagation();
                    const newCategories = categories.filter(c => c.id !== catId);
                    updateConfig('categories', newCategories);
                };

                const modeOptions = [
                    { value: 'auto', label: 'Auto (from scan data)', color: '#6b7280' },
                    { value: 'dataSource', label: 'Data Source', color: '#2563eb' },
                    { value: 'manual', label: 'Manual Override', color: '#9333ea' }
                ].filter(opt => {
                    // Hide Data Source for basic Combined Scan Grid - Keep for Advanced Grid
                    if (opt.value === 'dataSource' && block.widgetType === 'combined-scan-grid') return false;
                    return true;
                });

                const manualStatusOptions = [
                    { value: 'Green', label: 'Yes (Green)', color: '#16a34a' },
                    { value: 'Yellow', label: 'Maybe (Yellow)', color: '#eab308' },
                    { value: 'Red', label: 'No (Red)', color: '#dc2626' }
                ];

                const scoringTypes = [
                    { value: 'count', label: 'Count (# matching items)' },
                    { value: 'ratio', label: 'Ratio (% matching)' },
                    { value: 'threshold', label: 'Threshold (numeric value)' },
                    { value: 'match', label: 'Match (string contains)' },
                    { value: 'boolean', label: 'Boolean (exists/truthy)' }
                ];

                const filterOperators = [
                    { value: 'equals', label: 'equals' },
                    { value: 'not_equals', label: 'not equals' },
                    { value: 'contains', label: 'contains' },
                    { value: 'greater_than', label: 'greater than' },
                    { value: 'less_than', label: 'less than' },
                    { value: 'exists', label: 'exists' }
                ];

                const dataOverrides = block.config?.dataOverrides || {};
                const scanData = dataContext?.scanData || dataContext?.data || dataContext || {};
                const allSources = getAvailableDataSources(scanData);
                const scanGroupKeys = (scanGroups || []).map(g => g.key);
                const allowedSources = [...new Set(['Ad Scan', 'network_devices', 'localhost', 'MS365', 'PII', ...scanGroupKeys])];
                const availableSources = ['advanced-scan-grid', 'combined-scan-grid', 'infrastructure-scan-grid', 'security-scan-grid', 'managed-service-experience', 'cyber-security-threat-exposure'].includes(block.widgetType)
                    ? allSources.filter(src => allowedSources.includes(src))
                    : allSources;

                // Expanded category state (uses config to persist)
                const expandedCat = block.config?._expandedCategory || null;
                const setExpandedCat = (catId) => {
                    updateConfig('_expandedCategory', expandedCat === catId ? null : catId);
                };

                const getMode = (categoryId) => {
                    const override = dataOverrides[categoryId];
                    if (!override) return 'auto';
                    return override.mode || 'auto';
                };

                const getStatusColor = (categoryId) => {
                    const mode = getMode(categoryId);
                    if (mode === 'auto') return '#6b7280';
                    if (mode === 'manual') {
                        const status = dataOverrides[categoryId]?.status || 'Green';
                        return { 'Green': '#16a34a', 'Yellow': '#eab308', 'Red': '#dc2626' }[status] || '#6b7280';
                    }
                    if (mode === 'dataSource') return '#2563eb';
                    return '#6b7280';
                };

                const updateCategoryOverride = (categoryId, updates) => {
                    const current = dataOverrides[categoryId] || {};
                    const newOverrides = {
                        ...dataOverrides,
                        [categoryId]: { ...current, ...updates }
                    };
                    updateConfig('dataOverrides', newOverrides);
                };

                const handleModeChange = (categoryId, mode) => {
                    if (mode === 'auto') {
                        const newOverrides = { ...dataOverrides };
                        delete newOverrides[categoryId];
                        updateConfig('dataOverrides', newOverrides);
                    } else if (mode === 'manual') {
                        updateCategoryOverride(categoryId, { mode: 'manual', status: 'Green' });
                    } else if (mode === 'dataSource') {
                        updateCategoryOverride(categoryId, { 
                            mode: 'dataSource', 
                            source: '', 
                            path: [], 
                            filter: null,
                            scoring: { type: 'count', greenThreshold: 1, yellowThreshold: 1 }
                        });
                    }
                };

                const inputStyle = {
                    width: '100%', fontSize: '0.75rem', padding: '4px 6px',
                    border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: '#fff'
                };
                const labelStyle = {
                    fontSize: '0.7rem', fontWeight: '600', color: '#6b7280',
                    marginBottom: '2px', display: 'block'
                };

                return (
                    <div className="property-field" style={{ marginTop: '12px' }}>
                        {(block.widgetType === 'combined-scan-grid' || block.widgetType === 'advanced-scan-grid') && (
                            <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#f0f7ff', borderRadius: '6px', border: '1px solid #cce3ff' }}>
                                <span style={labelStyle}>Grid Type</span>
                                <select
                                    value={block.config?.gridType || (block.widgetType === 'advanced-scan-grid' ? 'custom' : 'infrastructure')}
                                    onChange={(e) => {
                                        const newGridType = e.target.value;
                                        let newDefaults = [];
                                        let newTitle = 'SCAN GRID';
                                        
                                        // Bridge legacy keys to backend scan_type keys
                                        const legacyMap = {
                                            'infrastructure': 'Infrastructure',
                                            'managed-service': 'Managed IT/Service Experience',
                                            'security': 'Security',
                                            'cyber-security': 'Cyber Security (Threat & Exposure)',
                                            'network-scan': 'Network Scan',
                                            'localhost-scan': 'Localhost Scan',
                                            'ad-scan': 'AD Scan',
                                            'ms365-scan': 'MS365 Scan',
                                            'pii-scan': 'PII Scan'
                                        };

                                        const backendKey = legacyMap[newGridType] || newGridType;
                                        const group = scanGroups.find(g => g.key === backendKey);

                                        if (newGridType === 'custom') {
                                            newTitle = 'CUSTOM SCAN GRID';
                                            newDefaults = [];
                                        } else if (group) {
                                            newTitle = group.label.toUpperCase();
                                            // Filter from allCategories using the dynamic scan_type
                                            newDefaults = allCategories
                                                .filter(cat => cat.scan_type === group.key)
                                                .map(cat => ({ 
                                                    id: cat.name, 
                                                    label: cat.name 
                                                }));
                                        }

                                        updateConfig('gridType', newGridType);
                                        updateConfig('categories', newDefaults);
                                        updateConfig('title', newTitle);
                                        // Reset overrides when switching types to avoid stale data
                                        updateConfig('dataOverrides', {});
                                    }}
                                    style={inputStyle}
                                >
                                    {block.widgetType === 'advanced-scan-grid' && <option value="custom">Custom (Start Blank)</option>}
                                    
                                    {/* Map dynamic scan groups */}
                                    {scanGroups.map(group => {
                                        // Find if this group matches any of our legacy keys for better UX labels if needed
                                        const label = group.label;
                                        const value = (group.key === 'Infrastructure') ? 'infrastructure' :
                                                      (group.key === 'Managed IT/Service Experience') ? 'managed-service' :
                                                      (group.key === 'Security') ? 'security' :
                                                      (group.key === 'Cyber Security (Threat & Exposure)') ? 'cyber-security' :
                                                      (group.key === 'Network Scan') ? 'network-scan' :
                                                      (group.key === 'Localhost Scan') ? 'localhost-scan' :
                                                      (group.key === 'AD Scan') ? 'ad-scan' :
                                                      (group.key === 'MS365 Scan') ? 'ms365-scan' :
                                                      (group.key === 'PII Scan') ? 'pii-scan' : group.key;

                                        return <option key={group.key} value={value}>{label}</option>;
                                    })}
                                </select>
                            </div>
                        )}

                        {!(block.widgetType === 'advanced-scan-grid' || block.widgetType === 'combined-scan-grid') && (
                            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                                Category Data Source Configuration
                            </label>
                        )}
                        
                        {(block.widgetType === 'infrastructure-scan-grid' || block.widgetType === 'managed-service-experience' || block.widgetType === 'security-scan-grid' || block.widgetType === 'cyber-security-threat-exposure') && (
                            <div style={{ marginBottom: '12px', fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                                Configure how individual items in the grid are calculated from the scan data.
                            </div>
                        )}

                        {/* Layout & Visibility Section */}
                        <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <span style={{ ...labelStyle, marginBottom: '8px', color: '#374151', fontSize: '0.75rem' }}>Layout & Visibility</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {(() => {
                                    const defaultLayout = [
                                        { id: 'title', label: 'Title', visible: true },
                                        { id: 'grid', label: 'Findings Grid', visible: true },
                                        { id: 'legend', label: 'Status Legend', visible: true },
                                        { id: 'score', label: 'Audit Score Bar', visible: true }
                                    ];
                                    const layout = block.config?.layout || defaultLayout;

                                    return layout.map((item, index) => (
                                        <div 
                                            key={item.id} 
                                            draggable={true}
                                            onDragStart={() => setDraggedItemIndex(index)}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={() => {
                                                if (draggedItemIndex === null || draggedItemIndex === index) return;
                                                const newLayout = [...layout];
                                                const draggedItem = newLayout[draggedItemIndex];
                                                newLayout.splice(draggedItemIndex, 1);
                                                newLayout.splice(index, 0, draggedItem);
                                                updateConfig('layout', newLayout);
                                                setDraggedItemIndex(null);
                                            }}
                                            onDragEnd={() => setDraggedItemIndex(null)}
                                            style={{ 
                                                display: 'flex', alignItems: 'center', gap: '8px', 
                                                padding: '8px 10px', backgroundColor: draggedItemIndex === index ? '#f0f4ff' : '#fff', 
                                                border: '1px solid #d1d5db', borderRadius: '4px',
                                                cursor: 'grab',
                                                opacity: draggedItemIndex === index ? 0.5 : 1,
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <div style={{ color: '#9ca3af', fontSize: '1rem', cursor: 'grab', userSelect: 'none' }}>⋮⋮</div>
                                            <input 
                                                type="checkbox" 
                                                checked={item.visible !== false}
                                                onChange={() => {
                                                    const newLayout = layout.map(l => 
                                                        l.id === item.id ? { ...l, visible: l.visible === false ? true : false } : l
                                                    );
                                                    updateConfig('layout', newLayout);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ cursor: 'pointer' }}
                                            />
                                            <span style={{ flex: 1, fontSize: '0.75rem', fontWeight: '500' }}>{item.label}</span>
                                        </div>
                                    ));
                                })()}
                            </div>
                            <small style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '6px', display: 'block' }}>
                                Drag the handle (⋮⋮) to reorder components.
                            </small>
                            
                            <hr style={{ margin: '12px 0', border: '0', borderTop: '1px solid #e5e7eb' }} />
                            
                        {(block.widgetType === 'advanced-scan-grid' || block.widgetType === 'combined-scan-grid') ? (
                            <span style={{ ...labelStyle, marginBottom: '8px', color: '#374151', fontSize: '0.75rem', marginTop: '16px' }}>Grid Items & Overrides</span>
                        ) : (
                            <span style={{ ...labelStyle, marginBottom: '8px', color: '#374151', fontSize: '0.75rem' }}>Finding Item Options</span>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={block.config?.showFindingHeaders !== false}
                                        onChange={(e) => updateConfig('showFindingHeaders', e.target.checked)}
                                    />
                                    <span style={{ fontSize: '0.75rem' }}>Show Headers</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={block.config?.showFindingDescriptions !== false}
                                        onChange={(e) => updateConfig('showFindingDescriptions', e.target.checked)}
                                    />
                                    <span style={{ fontSize: '0.75rem' }}>Show Descriptions</span>
                                </label>
                            </div>
                        </div>

                        <small style={{ display: 'block', marginBottom: '12px', color: '#6c757d' }}>
                            Click a category to configure its data source and scoring logic
                        </small>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {categories.map((cat, index) => {
                                const mode = getMode(cat.id);
                                const isExpanded = expandedCat === cat.id;
                                const override = dataOverrides[cat.id] || {};
                                const statusColor = getStatusColor(cat.id);
                                const isHidden = block.widgetType !== 'advanced-scan-grid' && index >= 9;
                                
                                return (
                                    <div 
                                        key={cat.id} 
                                        draggable={true}
                                        onDragStart={() => setDraggedItemIndex(index)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => {
                                            if (draggedItemIndex === null || draggedItemIndex === index) return;
                                            const newCategories = [...categories];
                                            const draggedItem = newCategories[draggedItemIndex];
                                            newCategories.splice(draggedItemIndex, 1);
                                            newCategories.splice(index, 0, draggedItem);
                                            updateConfig('categories', newCategories);
                                            setDraggedItemIndex(null);
                                        }}
                                        onDragEnd={() => setDraggedItemIndex(null)}
                                        style={{ 
                                            borderRadius: '6px', 
                                            overflow: 'hidden', 
                                            border: isHidden ? '1px dashed #d1d5db' : '1px solid #e5e7eb',
                                            opacity: (draggedItemIndex === index) ? 0.5 : (isHidden ? 0.75 : 1),
                                            backgroundColor: (draggedItemIndex === index) ? '#f0f4ff' : '#fff',
                                            marginBottom: '2px',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        {/* Category Header Row */}
                                        <div 
                                            onClick={() => setExpandedCat(cat.id)}
                                            style={{ 
                                                display: 'flex', alignItems: 'center', gap: '8px',
                                                padding: '8px 10px', cursor: 'pointer',
                                                backgroundColor: isExpanded ? '#f0f4ff' : (isHidden ? '#f9fafb' : '#f8f9fa'),
                                                borderLeft: `4px solid ${isHidden ? '#9ca3af' : statusColor}`,
                                                transition: 'background-color 0.15s'
                                            }}
                                        >
                                            <div style={{ color: '#9ca3af', fontSize: '1rem', cursor: 'grab', userSelect: 'none', marginRight: '4px' }}>⋮⋮</div>
                                            <span style={{ fontSize: '0.65rem', color: '#9ca3af', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>▶</span>
                                            <span style={{ 
                                                flex: 1, 
                                                fontSize: '0.8rem', 
                                                fontWeight: isHidden ? '400' : '500', 
                                                color: isHidden ? '#9ca3af' : '#374151',
                                                textDecoration: isHidden ? 'line-through' : 'none'
                                            }}>
                                                {override.displayName || cat.label}
                                                {isHidden && <span style={{ marginLeft: '8px', fontSize: '0.6rem', color: '#ef4444', fontWeight: '700', textDecoration: 'none', display: 'inline-block' }}>[HIDDEN - 9 ITEM LIMIT]</span>}
                                            </span>
                                            <span style={{ 
                                                fontSize: '0.65rem', padding: '1px 6px', borderRadius: '3px',
                                                backgroundColor: mode === 'auto' ? '#f3f4f6' : mode === 'dataSource' ? '#dbeafe' : '#f3e8ff',
                                                color: isHidden ? '#9ca3af' : statusColor, fontWeight: '600'
                                            }}>
                                                {mode === 'auto' ? 'AUTO' : mode === 'dataSource' ? 'DATA' : override.status || 'MANUAL'}
                                            </span>
                                            <button
                                                onClick={(e) => handleRemoveCategory(cat.id, e)}
                                                style={{
                                                    marginLeft: '8px', padding: '0 4px', fontSize: '0.9rem',
                                                    color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer',
                                                    visibility: 'visible',
                                                    opacity : 0.6,
                                                }}
                                                onMouseOver={(e) => e.target.style.opacity = '1'}
                                                onMouseOut={(e) => e.target.style.opacity = '0.6'}
                                                title="Remove Item"
                                            >
                                                ✕
                                            </button>
                                        </div>

                                        {/* Expanded Config Panel */}
                                        {isExpanded && (
                                            <div style={{ padding: '10px 12px', backgroundColor: '#fafbfc', borderTop: '1px solid #e5e7eb' }}>
                                                {/* Display Name Override */}
                                                <div style={{ marginBottom: '10px' }}>
                                                    <span style={labelStyle}>Display Name</span>
                                                    <input
                                                        type="text"
                                                        value={override.displayName || ''}
                                                        onChange={(e) => updateCategoryOverride(cat.id, { displayName: e.target.value })}
                                                        placeholder={cat.title}
                                                        style={inputStyle}
                                                    />
                                                </div>

                                                {/* Mode Selector */}
                                                <div style={{ marginBottom: '10px' }}>
                                                    <span style={labelStyle}>Mode</span>
                                                    <div style={{ display: 'flex', gap: '4px' }}>
                                                        {modeOptions.map(opt => (
                                                            <button
                                                                key={opt.value}
                                                                onClick={() => handleModeChange(cat.id, opt.value)}
                                                                style={{
                                                                    flex: 1, padding: '4px 6px', fontSize: '0.7rem',
                                                                    border: `1.5px solid ${mode === opt.value ? opt.color : '#d1d5db'}`,
                                                                    borderRadius: '4px', cursor: 'pointer',
                                                                    backgroundColor: mode === opt.value ? `${opt.color}15` : '#fff',
                                                                    color: mode === opt.value ? opt.color : '#6b7280',
                                                                    fontWeight: mode === opt.value ? '700' : '400'
                                                                }}
                                                            >
                                                                {opt.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>


                                                {/* Manual Mode */}
                                                {mode === 'manual' && (
                                                    <div>
                                                        <span style={labelStyle}>Status</span>
                                                        <select
                                                            value={override.status || 'Green'}
                                                            onChange={(e) => updateCategoryOverride(cat.id, { status: e.target.value })}
                                                            style={{ ...inputStyle, borderColor: getStatusColor(cat.id) }}
                                                        >
                                                            {manualStatusOptions.map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </select>
                                                        
                                                        <div style={{ marginTop: '10px' }}>
                                                            <span style={labelStyle}>Description (optional)</span>
                                                            <textarea
                                                                rows={2}
                                                                value={override.description || ''}
                                                                onChange={(e) => updateCategoryOverride(cat.id, { description: e.target.value })}
                                                                placeholder="Override default status description..."
                                                                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Data Source Mode */}
                                                {mode === 'dataSource' && (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                        {/* Source Selection */}
                                                        <div>
                                                            <span style={labelStyle}>Data Source</span>
                                                            <select
                                                                value={override.source || ''}
                                                                onChange={(e) => updateCategoryOverride(cat.id, { source: e.target.value, path: [], filter: null })}
                                                                style={inputStyle}
                                                            >
                                                                <option value="">-- Select source --</option>
                                                                {availableSources.map(src => (
                                                                    <option key={src} value={src}>{src}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        {/* Drill-Down Path (up to 3 levels) */}
                                                        {override.source && (() => {
                                                            const path = override.path || [];
                                                            const levels = [];
                                                            // Drill-down: unlimited depth, but stop when we reach an array.
                                                            // Arrays are handled by the Filter section below.
                                                            for (let i = 0; i <= path.length && i < 10; i++) {
                                                                const parentPath = path.slice(0, i);
                                                                // Stop if the current resolved path is an array — Filter handles it
                                                                if (i > 0 && isArrayAtPath(scanData, override.source, parentPath)) break;
                                                                const childKeys = getChildKeys(scanData, override.source, parentPath);
                                                                if (childKeys.length === 0) break;
                                                                levels.push({ depth: i, keys: childKeys, selected: path[i] || '' });
                                                            }
                                                            if (levels.length === 0) return null;
                                                            return (
                                                                <div>
                                                                    <span style={labelStyle}>Path (drill-down)</span>
                                                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                                        {levels.map((level, idx) => (
                                                                            <React.Fragment key={idx}>
                                                                                {idx > 0 && <span style={{ alignSelf: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>→</span>}
                                                                                <select
                                                                                    value={level.selected}
                                                                                    onChange={(e) => {
                                                                                        const newPath = [...path.slice(0, level.depth)];
                                                                                        if (e.target.value) newPath.push(e.target.value);
                                                                                        updateCategoryOverride(cat.id, { path: newPath, filter: null });
                                                                                    }}
                                                                                    style={{ ...inputStyle, flex: 1, minWidth: '80px' }}
                                                                                >
                                                                                    <option value="">-- key --</option>
                                                                                    {level.keys.map(k => (
                                                                                        <option key={k} value={k}>{k}</option>
                                                                                    ))}
                                                                                </select>
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Filter (for arrays) */}
                                                        {override.source && isArrayAtPath(scanData, override.source, override.path || []) && (
                                                            <div style={{ padding: '8px', backgroundColor: '#f0f4ff', borderRadius: '4px', border: '1px solid #dbeafe' }}>
                                                                <span style={{ ...labelStyle, color: '#2563eb' }}>Filter (array items)</span>
                                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                                    <select
                                                                        value={override.filter?.field || ''}
                                                                        onChange={(e) => updateCategoryOverride(cat.id, {
                                                                            filter: { ...override.filter, field: e.target.value }
                                                                        })}
                                                                        style={{ ...inputStyle, flex: 1 }}
                                                                    >
                                                                        <option value="">-- field --</option>
                                                                        {getChildKeys(scanData, override.source, override.path || []).map(k => (
                                                                            <option key={k} value={k}>{k}</option>
                                                                        ))}
                                                                    </select>
                                                                    <select
                                                                        value={override.filter?.operator || 'equals'}
                                                                        onChange={(e) => updateCategoryOverride(cat.id, {
                                                                            filter: { ...override.filter, operator: e.target.value }
                                                                        })}
                                                                        style={{ ...inputStyle, width: '90px', flex: 'none' }}
                                                                    >
                                                                        {filterOperators.map(op => (
                                                                            <option key={op.value} value={op.value}>{op.label}</option>
                                                                        ))}
                                                                    </select>
                                                                    {override.filter?.operator !== 'exists' && (() => {
                                                                        const field = override.filter?.field;
                                                                        const uniqueValues = field ? getUniqueValues(scanData, override.source, override.path || [], field) : [];
                                                                        const isOpen = openFilterId === cat.id;
                                                                        
                                                                        return (
                                                                            <div className="filter-dropdown-container" style={{ position: 'relative', flex: 1, display: 'flex' }}>
                                                                                <input
                                                                                    type="text"
                                                                                    value={override.filter?.value || ''}
                                                                                    onChange={(e) => updateCategoryOverride(cat.id, {
                                                                                        filter: { ...override.filter, value: e.target.value }
                                                                                    })}
                                                                                    onFocus={() => {
                                                                                        if (!isOpen && uniqueValues.length > 0) setOpenFilterId(cat.id);
                                                                                    }}
                                                                                    placeholder={uniqueValues.length > 0 ? `e.g. ${uniqueValues.find(v => v !== '') || uniqueValues[0]}` : "value"}
                                                                                    style={{ ...inputStyle, flex: 1, borderRadius: '4px 0 0 4px', borderRight: 'none' }}
                                                                                />
                                                                                {uniqueValues.length > 0 && (
                                                                                    <button
                                                                                        onClick={() => setOpenFilterId(isOpen ? null : cat.id)}
                                                                                        style={{
                                                                                            border: '1px solid #d1d5db', borderLeft: 'none', borderRadius: '0 4px 4px 0',
                                                                                            backgroundColor: '#f9fafb', padding: '0 6px', cursor: 'pointer',
                                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                                                        }}
                                                                                    >
                                                                                        <span style={{ fontSize: '0.6rem', color: '#6b7280' }}>▼</span>
                                                                                    </button>
                                                                                )}
                                                                                
                                                                                {isOpen && uniqueValues.length > 0 && (
                                                                                    <div style={{
                                                                                        position: 'absolute', top: '100%', left: 0, right: 0,
                                                                                        backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px',
                                                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', zIndex: 50,
                                                                                        maxHeight: '150px', overflowY: 'auto', marginTop: '4px'
                                                                                    }}>
                                                                                        {uniqueValues.map(val => (
                                                                                            <div
                                                                                                key={val}
                                                                                                onClick={() => {
                                                                                                    updateCategoryOverride(cat.id, {
                                                                                                        filter: { ...override.filter, value: String(val) }
                                                                                                    });
                                                                                                    setOpenFilterId(null);
                                                                                                }}
                                                                                                style={{
                                                                                                    padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer',
                                                                                                    color: '#374151', borderBottom: '1px solid #f3f4f6'
                                                                                                }}
                                                                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                                                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                                                                            >
                                                                                                {val === '' ? <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>(Blank)</span> : String(val)}
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Filter (for leaf/primitive values) */}
                                                        {override.source && isLeafAtPath(scanData, override.source, override.path || []) && (() => {
                                                            const leafValue = resolveDataPath(scanData, override.source, override.path || []);
                                                            return (
                                                                <div style={{ padding: '8px', backgroundColor: '#fef3c7', borderRadius: '4px', border: '1px solid #fde68a' }}>
                                                                    <span style={{ ...labelStyle, color: '#92400e' }}>Filter (leaf value)</span>
                                                                    <div style={{ fontSize: '0.7rem', color: '#78350f', marginBottom: '6px' }}>
                                                                        Current value: <strong>{String(leafValue)}</strong>
                                                                    </div>
                                                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                                        <select
                                                                            value={override.filter?.operator || 'equals'}
                                                                            onChange={(e) => updateCategoryOverride(cat.id, {
                                                                                filter: { ...override.filter, field: '__leaf__', operator: e.target.value }
                                                                            })}
                                                                            style={{ ...inputStyle, width: '90px', flex: 'none' }}
                                                                        >
                                                                            {filterOperators.map(op => (
                                                                                <option key={op.value} value={op.value}>{op.label}</option>
                                                                            ))}
                                                                        </select>
                                                                        {override.filter?.operator !== 'exists' && (
                                                                            <input
                                                                                type="text"
                                                                                value={override.filter?.value || ''}
                                                                                onChange={(e) => updateCategoryOverride(cat.id, {
                                                                                    filter: { ...override.filter, field: '__leaf__', value: e.target.value }
                                                                                })}
                                                                                placeholder={String(leafValue)}
                                                                                style={{ ...inputStyle, flex: 1 }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Scoring Rule */}
                                                        <div style={{ padding: '8px', backgroundColor: '#f0fdf4', borderRadius: '4px', border: '1px solid #bbf7d0' }}>
                                                            <span style={{ ...labelStyle, color: '#16a34a' }}>Scoring Rule</span>
                                                            <select
                                                                value={override.scoring?.type || 'count'}
                                                                onChange={(e) => updateCategoryOverride(cat.id, {
                                                                    scoring: { ...override.scoring, type: e.target.value }
                                                                })}
                                                                style={{ ...inputStyle, marginBottom: '6px' }}
                                                            >
                                                                {scoringTypes.map(st => (
                                                                    <option key={st.value} value={st.value}>{st.label}</option>
                                                                ))}
                                                            </select>

                                                            {/* Threshold inputs for count/ratio/threshold */}
                                                            {['count', 'ratio', 'threshold'].includes(override.scoring?.type || 'count') && (() => {
                                                                const isRatio = override.scoring?.type === 'ratio';
                                                                // Calculate potential max value for "Use Max" helper
                                                                let maxVal = 0;
                                                                if (override.source) {
                                                                    const d = resolveDataPath(scanData, override.source, override.path || []);
                                                                    if (Array.isArray(d)) maxVal = d.length;
                                                                }

                                                                return (
                                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                                        <div style={{ flex: 1 }}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                <span style={{ ...labelStyle, fontSize: '0.65rem' }}>
                                                                                    Green ≥ {isRatio ? '%' : ''}
                                                                                </span>
                                                                                {!isRatio && maxVal > 0 && (
                                                                                    <button 
                                                                                        onClick={() => updateCategoryOverride(cat.id, {
                                                                                            scoring: { ...override.scoring, greenThreshold: maxVal }
                                                                                        })}
                                                                                        style={{ 
                                                                                            fontSize: '0.6rem', padding: '0 4px', 
                                                                                            border: '1px solid #dbeafe', borderRadius: '3px',
                                                                                            backgroundColor: '#eff6ff', color: '#2563eb', cursor: 'pointer'
                                                                                        }}
                                                                                        title={`Set to max count (${maxVal})`}
                                                                                    >
                                                                                        Max: {maxVal}
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                            <input
                                                                                type="number"
                                                                                value={override.scoring?.greenThreshold ?? (isRatio ? 80 : 1)}
                                                                                onChange={(e) => updateCategoryOverride(cat.id, {
                                                                                    scoring: { ...override.scoring, greenThreshold: parseFloat(e.target.value) || 0 }
                                                                                })}
                                                                                style={inputStyle}
                                                                            />
                                                                        </div>
                                                                        <div style={{ flex: 1 }}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                <span style={{ ...labelStyle, fontSize: '0.65rem' }}>
                                                                                    Yellow ≥ {isRatio ? '%' : ''}
                                                                                </span>
                                                                                {!isRatio && maxVal > 0 && (
                                                                                    <button 
                                                                                        onClick={() => updateCategoryOverride(cat.id, {
                                                                                            scoring: { ...override.scoring, yellowThreshold: maxVal }
                                                                                        })}
                                                                                        style={{ 
                                                                                            fontSize: '0.6rem', padding: '0 4px', 
                                                                                            border: '1px solid #fff7ed', borderRadius: '3px',
                                                                                            backgroundColor: '#fff7ed', color: '#c2410c', cursor: 'pointer'
                                                                                        }}
                                                                                        title={`Set to max count (${maxVal})`}
                                                                                    >
                                                                                        Max: {maxVal}
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                            <input
                                                                                type="number"
                                                                                value={override.scoring?.yellowThreshold ?? (isRatio ? 50 : 1)}
                                                                                onChange={(e) => updateCategoryOverride(cat.id, {
                                                                                    scoring: { ...override.scoring, yellowThreshold: parseFloat(e.target.value) || 0 }
                                                                                })}
                                                                                style={inputStyle}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}

                                                            {/* Match value for match scoring */}
                                                            {override.scoring?.type === 'match' && (
                                                                <div style={{ marginTop: '6px' }}>
                                                                    <span style={{ ...labelStyle, fontSize: '0.65rem' }}>Match Value</span>
                                                                    <input
                                                                        type="text"
                                                                        value={override.scoring?.matchValue || ''}
                                                                        onChange={(e) => updateCategoryOverride(cat.id, {
                                                                            scoring: { ...override.scoring, matchValue: e.target.value }
                                                                        })}
                                                                        placeholder="e.g. Server 2019"
                                                                        style={inputStyle}
                                                                    />
                                                                </div>
                                                            )}

                                                            {/* Custom description removed - handled by Threshold Note Customization below */}

                                                            {/* Threshold Note Customization */}
                                                            <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
                                                                <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block', fontSize: '0.75rem' }}>Threshold Note Customization</label>
                                                                
                                                                {(() => {
                                                                    const customNotes = override.customNotes || {};
                                                                    const thresholdVars = [
                                                                        { label: 'Count', value: '{count}' },
                                                                        { label: 'Total', value: '{total}' },
                                                                        { label: 'Percentage', value: '{percent}' }
                                                                    ];

                                                                    // Resolve category name for threshold lookup
                                                                    const catLabel = override.autoSourceId || cat.label;
                                                                    
                                                                    // Enhanced Threshold Lookup (Exact match -> Case-insensitive -> Trimmed match)
                                                                    const getCatThreshold = () => {
                                                                        if (!dataContext?.category_thresholds) return null;
                                                                        const thresholds = dataContext.category_thresholds;
                                                                        const cleanLabel = catLabel?.trim();
                                                                        const lowerLabel = cleanLabel?.toLowerCase();
                                                                        
                                                                        // 1. Direct hit
                                                                        if (thresholds[cleanLabel]) return thresholds[cleanLabel];
                                                                        
                                                                        // 2. Case-insensitive exact match
                                                                        const exactMatch = Object.entries(thresholds).find(([k]) => k.trim().toLowerCase() === lowerLabel);
                                                                        if (exactMatch) return exactMatch[1];

                                                                        // 3. Fuzzy search (includes)
                                                                        const fuzzyMatch = Object.entries(thresholds).find(([k]) => {
                                                                            const key = k.trim().toLowerCase();
                                                                            return key.includes(lowerLabel) || lowerLabel.includes(key);
                                                                        });
                                                                        
                                                                        return fuzzyMatch ? fuzzyMatch[1] : null;
                                                                    };
                                                                    
                                                                    const catThreshold = getCatThreshold();
                                                                    const rules = catThreshold?.rules || {};

                                                                    // Map to standard tiers
                                                                    const statusToTier = {
                                                                        'Critical': 'Bad',
                                                                        'Warning': 'Moderate',
                                                                        'Healthy': 'Healthy' 
                                                                    };

                                                                    const updateStatusNote = (status, val) => {
                                                                        updateCategoryOverride(cat.id, { 
                                                                            customNotes: { ...customNotes, [status]: val }
                                                                        });
                                                                    };

                                                                    const TemplateNoteSelectorLocal = ({ status }) => {
                                                                        const tier = statusToTier[status];
                                                                        const tierKeys = tier === 'Healthy' ? ['Healthy', 'Good'] : [tier];
                                                                        
                                                                        let displayNotes = [];
                                                                        const seenTexts = new Set();
                                                                        
                                                                        const addNote = (noteObj, sourceLabel) => {
                                                                            if (!noteObj || !noteObj.text || seenTexts.has(noteObj.text)) return;
                                                                            displayNotes.push({ 
                                                                                ...noteObj, 
                                                                                sourceLabel: sourceLabel + (noteObj.isDefault ? ' Default' : '')
                                                                            });
                                                                            seenTexts.add(noteObj.text);
                                                                        };

                                                                        tierKeys.forEach(t => {
                                                                            const tRules = rules[t] || {};
                                                                            const aRules = catThreshold?.default_rules ? catThreshold.default_rules[t] : null;
                                                                            
                                                                            if (Array.isArray(tRules.notes)) tRules.notes.forEach(n => addNote(n, 'Custom'));
                                                                            if (aRules && Array.isArray(aRules.notes)) aRules.notes.forEach(n => addNote(n, 'App Admin'));
                                                                            if (tRules.note) addNote({ text: tRules.note }, 'Custom');
                                                                            if (aRules?.note) addNote({ text: aRules.note }, 'App Admin');
                                                                        });

                                                                        if (displayNotes.length === 0) return null;

                                                                        return (
                                                                            <select
                                                                                className="form-select form-select-sm"
                                                                                style={{ fontSize: '0.7rem', flex: 1, marginBottom: '2px', padding: '2px 4px', height: '24px', cursor: 'pointer', border: '1px solid #0d6efd', color: '#0d6efd', backgroundColor: '#f0f7ff', borderRadius: '4px' }}
                                                                                value=""
                                                                                onChange={(e) => {
                                                                                    const val = e.target.value;
                                                                                    if (val) updateStatusNote(status, val);
                                                                                }}
                                                                            >
                                                                                <option value="">Select Template...</option>
                                                                                {displayNotes.map((n, idx) => (
                                                                                    <option key={idx} value={n.text}>
                                                                                        {n.text.length > 50 ? n.text.substring(0, 47) + '...' : n.text} ({n.sourceLabel})
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        );
                                                                    };

                                                                    const ThresholdVarSelectorLocal = ({ status }) => (
                                                                        <select 
                                                                            className="form-select form-select-sm"
                                                                            style={{ fontSize: '0.7rem', width: 'auto', alignSelf: 'flex-end', marginBottom: '2px', padding: '2px 4px', height: '24px', cursor: 'pointer', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                                                            value=""
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                if (!val) return;
                                                                                const currentVal = customNotes[status] || '';
                                                                                const textareaId = `textarea-${cat.id}-${status}`;
                                                                                const el = document.getElementById(textareaId);
                                                                                let newVal = currentVal;
                                                                                if (el) {
                                                                                    const start = el.selectionStart;
                                                                                    const end = el.selectionEnd;
                                                                                    newVal = currentVal.substring(0, start) + val + currentVal.substring(end);
                                                                                    updateStatusNote(status, newVal);
                                                                                    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(start + val.length, start + val.length); } }, 0);
                                                                                } else {
                                                                                    updateStatusNote(status, currentVal + val);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <option value="">+ Variable</option>
                                                                            {thresholdVars.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                                                                        </select>
                                                                    );

                                                                    return (
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                                                                            {/* Removed redundant category selection - handled by Source Logic */}
                                                                            {[
                                                                                { label: 'Critical (Red)', status: 'Critical', color: '#dc2626', bgColor: '#fef2f2' },
                                                                                { label: 'Warning (Yellow)', status: 'Warning', color: '#eab308', bgColor: '#fffbeb' },
                                                                                { label: 'Healthy (Green)', status: 'Healthy', color: '#16a34a', bgColor: '#ecfdf5' }
                                                                            ].map(tier => {
                                                                                // Auto-patch missing notes from defaults if available
                                                                                if (!customNotes[tier.status] && catThreshold) {
                                                                                    const tName = statusToTier[tier.status];
                                                                                    const tRules = rules[tName] || {};
                                                                                    const aRules = catThreshold?.default_rules ? catThreshold.default_rules[tName] : null;
                                                                                    
                                                                                    // Priority: 1. rules.note -> 2. rules.notes[0] -> 3. default_rules.note -> 4. default_rules.notes[0]
                                                                                    const defaultNote = tRules.note || 
                                                                                                      (Array.isArray(tRules.notes) && tRules.notes[0]?.text) ||
                                                                                                      (aRules?.note) ||
                                                                                                      (Array.isArray(aRules?.notes) && aRules.notes[0]?.text);
                                                                                    
                                                                                    if (defaultNote) {
                                                                                        // Use a microtask to avoid updating state during render
                                                                                        setTimeout(() => {
                                                                                            updateStatusNote(tier.status, defaultNote);
                                                                                        }, 0);
                                                                                    }
                                                                                }

                                                                                return (
                                                                                    <div key={tier.status} className="property-field" style={{ marginBottom: '12px' }}>
                                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '8px', marginBottom: '4px' }}>
                                                                                            <label style={{ color: tier.color, fontSize: '0.7rem', fontWeight: '600', marginBottom: 0 }}>{tier.label}</label>
                                                                                            <TemplateNoteSelectorLocal status={tier.status} />
                                                                                            <ThresholdVarSelectorLocal status={tier.status} />
                                                                                        </div>
                                                                                        <textarea
                                                                                            id={`textarea-${cat.id}-${tier.status}`}
                                                                                            rows={2}
                                                                                            value={customNotes[tier.status] || ''}
                                                                                            onChange={(e) => updateStatusNote(tier.status, e.target.value)}
                                                                                            placeholder={`Enter custom note for ${tier.status.toLowerCase()} status...`}
                                                                                            style={{ ...inputStyle, width: '100%', fontSize: '0.7rem', padding: '4px', borderRadius: '4px', backgroundColor: tier.bgColor, borderColor: tier.color + '40', resize: 'vertical' }}
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Auto Mode info */}
                                                {mode === 'auto' && (() => {
                                                    // Map each auto source to structured data (mirroring Data Source mode layout)
                                                    const autoSourceStructured = {
                                                        'infrastructure-1': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Hardware Informations', 'OS'] },
                                                                { source: 'localhost', path: ['OS Version'] },
                                                                { source: 'network_devices', path: ['(each device)', 'system', 'OS'] }
                                                            ],
                                                            filter: 'Flags unsupported versions: 2003, 2008, 2012',
                                                            scoring: 'Green = ≥ 90% supported · Yellow = ≥ 70% · Red = < 70%'
                                                        },
                                                        'infrastructure-2': {
                                                            dataSources: [
                                                                { source: 'Ad Scan', path: ['Computers', '(each entry)'] }
                                                            ],
                                                            filter: 'account_flags includes WORKSTATION_TRUST_ACCOUNT · OS ≠ Server',
                                                            scoring: 'Green = ≥ 90% newer than 3yrs · Yellow = ≥ 70% · Red = < 70%'
                                                        },
                                                        'infrastructure-3': {
                                                            dataSources: [
                                                                { source: 'Ad Scan', path: ['Domain Controllers'] }
                                                            ],
                                                            filter: null,
                                                            scoring: 'Green = 2+ DCs (100%) · Red = 1 DC (0%)'
                                                        },
                                                        'infrastructure-4': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Hardware Informations', 'Logical Disks', 'Percent Used'] },
                                                                { source: 'localhost', path: ['RAM'] }
                                                            ],
                                                            filter: null,
                                                            scoring: 'Green = ≥ 90% of disks healthy · Red = < 70%'
                                                        },
                                                        'infrastructure-5': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['installed_softwares'] }
                                                            ],
                                                            filter: 'Looks for Hyper-V or VMware entries',
                                                            scoring: 'Green = Found (100%) · Unknown/Red = Not Found'
                                                        },
                                                        'infrastructure-6': {
                                                            dataSources: [
                                                                { source: 'network_devices', path: ['(each device)', 'type'] },
                                                                { source: 'network_devices', path: ['(each device)', 'device_type'] }
                                                            ],
                                                            filter: 'type = Firewall, Switch, or Router',
                                                            scoring: 'Green = Found (100%) · Unknown/Red = Not Found'
                                                        },
                                                        'infrastructure-7': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Firewall'] },
                                                                { source: 'localhost', path: ['Non-Default Services'] }
                                                            ],
                                                            filter: null,
                                                            scoring: 'Green = Config Found (100%) · Unknown = Not Found'
                                                        },
                                                        'infrastructure-8': {
                                                            dataSources: [
                                                                { source: 'MS365', path: [] }
                                                            ],
                                                            filter: null,
                                                            scoring: 'Green = Scanned (100%) · Unknown = Not Scanned'
                                                        },
                                                        'infrastructure-9': {
                                                            dataSources: [
                                                                { source: '(composite)', path: ['Capacity (#4)', 'Redundancy (#3)', 'Workstation Age (#2)'] }
                                                            ],
                                                            filter: null,
                                                            scoring: 'Avg of scores: Green ≥ 90 · Yellow ≥ 70 · Red < 70'
                                                        },
                                                        'infrastructure-hw-warranty': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Hardware Informations', 'Model'] },
                                                                { source: 'localhost', path: ['Hardware Informations', 'Manufacturer'] }
                                                            ],
                                                            filter: 'Detects physical hardware models; excludes virtual machines',
                                                            scoring: 'Green = Info Found (100%) · Unknown = No Data'
                                                        },
                                                        // Managed Service Experience Categories
                                                        'managed-service-1': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['System Events', 'Critical'] },
                                                                { source: 'Reliability Monitor', path: ['Crash Frequency'] }
                                                            ],
                                                            filter: 'Event ID = 41, 1001 (Unexpected Shutdowns)',
                                                            scoring: 'Green (100%) = 0 events · Red (0%) = > 0 events'
                                                        },
                                                        'managed-service-2': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Security Events', 'Logon'] }
                                                            ],
                                                            filter: 'Duration > 30s',
                                                            scoring: 'Green (100%) = < 5s avg · Yellow (50%) = > 15s'
                                                        },
                                                        'managed-service-3': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Application Events', 'Errors'] }
                                                            ],
                                                            filter: 'Level = Error',
                                                            scoring: 'Green (100%) = 0 errors · Red (0%) = Errors found'
                                                        },
                                                        'managed-service-4': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Windows Update', 'History'] }
                                                            ],
                                                            filter: 'Status = Failed',
                                                            scoring: 'Green (100%) = 0 failures · Red (0%) = Failures found'
                                                        },
                                                        'managed-service-5': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Hardware Informations', 'Logical Disks', 'Percent Used'] }
                                                            ],
                                                            filter: 'Drive Type = Local Fixed Disk',
                                                            scoring: 'Red (50%) = > 90% used · Green (100%) = < 90%'
                                                        },
                                                        'managed-service-6': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Network Adapter', 'Packet Loss'] }
                                                            ],
                                                            filter: 'Ping Test',
                                                            scoring: 'Green (100%) = 0% loss · Red (0%) = loss detected'
                                                        },
                                                        'managed-service-7': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Hardware Informations', 'Firewall'] },
                                                                { source: 'localhost', path: ['Hardware Informations', 'Antivirus'] }
                                                            ],
                                                            filter: 'Status = Enabled',
                                                            scoring: 'Green (100%) = Both Active · Yellow (50%) = One Inactive'
                                                        },
                                                        'managed-service-8': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Event Logs', 'Patterns'] }
                                                            ],
                                                            filter: 'Recurring signature',
                                                            scoring: 'Green (100%) = None · Red (0%) = Detected'
                                                        },
                                                        'managed-service-9': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['System Uptime', 'Unexpected Shutdowns'] }
                                                            ],
                                                            filter: 'Shutdown Type = Unexpected',
                                                            scoring: 'Green (100%) = 0 · Red (0%) = > 0'
                                                        },
                                                        // Security Scan Grid Categories
                                                        'security-1': {
                                                            dataSources: [
                                                                { source: 'Localhost Scan', path: ['MFA Status'] },
                                                                { source: 'AD Scan', path: ['Users', '(each entry)', 'MFA Enabled'] }
                                                            ],
                                                            filter: 'Checks for MFA enforcement on critical accounts',
                                                            scoring: 'Green = 100% Coverage · Yellow = Partial · Red = Critical Gaps'
                                                        },
                                                        'security-2': {
                                                            dataSources: [
                                                                { source: 'AD Scan', path: ['Privileged Groups', 'Members'] }
                                                            ],
                                                            filter: 'Monitors Domain Admins, Enterprise Admins, etc.',
                                                            scoring: 'Green = All secured · Yellow = High count · Red = Unauthorized'
                                                        },
                                                        'security-3': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Hardware Informations', 'Antivirus'] }
                                                            ],
                                                            filter: 'Checks if AV is active and signatures are current',
                                                            scoring: 'Green = Active & Up-to-date · Yellow = Drift · Red = Missing/Disabled'
                                                        },
                                                        'security-4': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Windows Update', 'History'] }
                                                            ],
                                                            filter: 'Checks for missing Security Updates (KB patches)',
                                                            scoring: 'Green = Current · Yellow = Pending · Red = Missing Critical'
                                                        },
                                                        'security-5': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['Hardware Informations', 'BitLocker'] }
                                                            ],
                                                            filter: 'Checks ProtectionStatus = 1',
                                                            scoring: 'Green = 100% Encrypted · Yellow = Mobile Unencrypted · Red = No Encryption'
                                                        },
                                                        'security-6': {
                                                            dataSources: [
                                                                { source: 'AD Scan', path: ['Users', '(each entry)', 'LastLogon'] }
                                                            ],
                                                            filter: 'LastLogon > 90 days AND enabled = true',
                                                            scoring: 'Green = 0 detected · Yellow = 1-5 detected · Red = > 5 detected'
                                                        },
                                                        'security-7': {
                                                            dataSources: [
                                                                { source: 'AD Scan', path: ['Users', '(each entry)', 'name'] }
                                                            ],
                                                            filter: "Looks for names like 'Admin', 'Guest', 'Shared'",
                                                            scoring: 'Green = 0 detected · Yellow = Testing accounts · Red = Widespread usage'
                                                        },
                                                        'security-8': {
                                                            dataSources: [
                                                                { source: 'AD Scan', path: ['Password Policy'] }
                                                            ],
                                                            filter: 'Checks min length, complexity, age',
                                                            scoring: 'Green = Strong · Yellow = Minor drift · Red = Weak/Missing'
                                                        },
                                                        'security-9': {
                                                            dataSources: [
                                                                { source: 'localhost', path: ['installed_softwares'] }
                                                            ],
                                                            filter: 'Blacklist check: Torrent, Remote Access (unauthorized), etc.',
                                                            scoring: 'Green = None · Yellow = PUPs · Red = High-risk detected'
                                                        },
                                                        // Cyber Security (Threat & Exposure) Categories
                                                        'cyber-1': {
                                                            dataSources: [
                                                                { source: 'network_devices', path: ['(each device)', 'ports'] }
                                                            ],
                                                            filter: 'Checks for critical exposed ports (21, 23, 445, 3389, etc.)',
                                                            scoring: 'Green = 0 risky ports · Red = > 0 detected'
                                                        },
                                                        'cyber-2': {
                                                            dataSources: [
                                                                { source: 'AD Scan', path: ['Ad Users Audit', '(each user)'] }
                                                            ],
                                                            filter: 'Privileged Groups (Domain Admins, etc.) AND MfaStatus ≠ Enabled',
                                                            scoring: 'Green = 0 unprotected admins · Red = > 0 detected'
                                                        },
                                                        'cyber-3': {
                                                            dataSources: [
                                                                { source: 'Localhost Scan', path: ['System Informations'] }
                                                            ],
                                                            filter: 'Checks for SMBv1 or NTLMv1 enabled in system configuration',
                                                            scoring: 'Green = Legacy auth disabled · Red = Enabled'
                                                        },
                                                        'cyber-4': {
                                                            dataSources: [
                                                                { source: 'Localhost Scan', path: ['Installed Applications'] }
                                                            ],
                                                            filter: 'Matches applications against high/critical risk or CVE data',
                                                            scoring: 'Green = 0 high-risk apps · Red = > 0 detected'
                                                        },
                                                        'cyber-5': {
                                                            dataSources: [
                                                                { source: 'Localhost Scan', path: ['Local Admins'] }
                                                            ],
                                                            filter: 'Checks for excessive local administrative rights (> 5)',
                                                            scoring: 'Green = 0-5 admins · Red = > 5 detected'
                                                        },
                                                        'cyber-6': {
                                                            dataSources: [
                                                                { source: 'MS365 Scan', path: ['ForwardingRules', 'DmarcIssues'] }
                                                            ],
                                                            filter: 'Detects risky forwarding rules or missing email security records',
                                                            scoring: 'Green = 0 issues · Red = > 0 detected'
                                                        },
                                                        'cyber-7': {
                                                            dataSources: [
                                                                { source: 'Localhost Scan', path: ['Startup Items', 'Scheduled Tasks'] }
                                                            ],
                                                            filter: 'Detects items flagged with suspicious status',
                                                            scoring: 'Green = 0 suspicious items · Red = > 0 detected'
                                                        },
                                                        'cyber-8': {
                                                            dataSources: [
                                                                { source: 'network_devices', path: ['(each device)', 'protocols'] }
                                                            ],
                                                            filter: 'Detects usage of deprecated protocols (TLS 1.0, TLS 1.1)',
                                                            scoring: 'Green = Strong encryption only · Red = Deprecated used'
                                                        },
                                                        'cyber-9': {
                                                            dataSources: [
                                                                { source: 'AD Scan', path: ['Ad Groups Audit', 'Domain Admins'] }
                                                            ],
                                                            filter: 'Checks for excessive members in highly privileged groups (> 10)',
                                                            scoring: 'Green = < 10 members · Red = ≥ 10 members'
                                                        }
                                                    };

                                                    // For Advanced Scan Grid, combine all possible default categories
                                                    const isAdvanced = block.widgetType === 'advanced-scan-grid';
                                                    let combinedDefaultCategories = defaultCategories;
                                                    
                                                    if (isAdvanced) {
                                                        const otherGroups = (scanGroups || []).map(g => g.label);
                                                        const otherDefaultItems = MANDATORY_CATEGORIES.map(catName => {
                                                            const scanType = SCAN_TYPE_MAPPING[catName] || 'Other';
                                                            return { id: catName, label: catName, group: scanType };
                                                        }).filter(item => otherGroups.includes(item.group));

                                                        // Add all categories from metadata discovery
                                                        const metadataItems = (allCategories || []).map(cat => ({
                                                            id: cat.label,
                                                            label: cat.label,
                                                            group: cat.scan_type
                                                        }));

                                                        // Add dynamic findings from scanData
                                                        const dynamicFindings = recreateFindingsArray(scanData);
                                                        const dynamicItems = dynamicFindings.map(f => {
                                                            const name = f[0];
                                                            const scanType = f[6] || SCAN_TYPE_MAPPING[name] || 'Other';
                                                            return { id: name, label: name, group: scanType };
                                                        });

                                                        const allDefaults = [
                                                            ...DEFAULT_INFRASTRUCTURE_CATEGORIES.map(c => ({ ...c, group: 'Infrastructure' })),
                                                            ...DEFAULT_MANAGED_SERVICE_CATEGORIES.map(c => ({ ...c, group: 'Managed IT' })),
                                                            ...DEFAULT_SECURITY_CATEGORIES.map(c => ({ ...c, group: 'Security' })),
                                                            ...DEFAULT_CYBER_SECURITY_CATEGORIES.map(c => ({ ...c, group: 'Cyber Security' })),
                                                            ...otherDefaultItems,
                                                            ...metadataItems,
                                                            ...dynamicItems
                                                        ];

                                                        // Deduplicate by ID
                                                        const seen = new Set();
                                                        combinedDefaultCategories = allDefaults.filter(item => {
                                                            if (seen.has(item.id)) return false;
                                                            seen.add(item.id);
                                                            return true;
                                                        });
                                                    }

                                                    const defaultMatchedCategory = combinedDefaultCategories.find(c => c.id === cat.id || c.label === cat.label || c.id === cat.label);
                                                    const selectedSourceId = override.autoSourceId || (defaultMatchedCategory ? defaultMatchedCategory.id : '');
                                                    const sourceInfo = autoSourceStructured[selectedSourceId];

                                                    // Map combinedDefaultCategories to searchable options with grouping
                                                    const searchableOptions = combinedDefaultCategories.map(c => ({
                                                        label: c.label,
                                                        value: c.id,
                                                        category: c.group
                                                    }));

                                                    return (
                                                        <>
                                                            <div style={{ marginTop: '10px' }}>
                                                                <span style={labelStyle}>Source Logic</span>
                                                                <SearchableSelect
                                                                    value={selectedSourceId}
                                                                    options={searchableOptions}
                                                                    placeholder="Select or search source logic..."
                                                                        onChange={(val) => {
                                                                            const selected = combinedDefaultCategories.find(c => c.id === val) || EXTRA_AUTO_SOURCES.find(c => c.id === val);
                                                                            let type = selected?.group;
                                                                            if (!type && selected) {
                                                                                type = SCAN_TYPE_MAPPING[selected.label] || 'Other';
                                                                            }
                                                                            updateCategoryOverride(cat.id, { 
                                                                                autoSourceId: val,
                                                                                autoSourceType: type
                                                                            });
                                                                        }}
                                                                />
                                                            </div>

                                                            {/* Structured data source display (mirrors Data Source mode) */}
                                                            {sourceInfo && (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                                                                    {/* Data Sources as dropdowns */}
                                                                    {sourceInfo.dataSources.map((ds, dsIdx) => (
                                                                        <div key={dsIdx}>
                                                                            {dsIdx === 0 && <span style={labelStyle}>Data Source</span>}
                                                                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center', marginTop: dsIdx > 0 ? '4px' : '0' }}>
                                                                                <select
                                                                                    disabled
                                                                                    value={ds.source}
                                                                                    style={{ ...inputStyle, flex: 'none', minWidth: '100px', opacity: 0.85, backgroundColor: '#f0f4ff', cursor: 'default' }}
                                                                                >
                                                                                    <option value={ds.source}>{ds.source}</option>
                                                                                </select>
                                                                                {ds.path.map((seg, segIdx) => (
                                                                                    <React.Fragment key={segIdx}>
                                                                                        <span style={{ alignSelf: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>→</span>
                                                                                        <select
                                                                                            disabled
                                                                                            value={seg}
                                                                                            style={{ ...inputStyle, flex: 1, minWidth: '80px', opacity: 0.85, backgroundColor: '#f0f4ff', cursor: 'default' }}
                                                                                        >
                                                                                            <option value={seg}>{seg}</option>
                                                                                        </select>
                                                                                    </React.Fragment>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    ))}

                                                                    {/* Filter section */}
                                                                    {sourceInfo.filter && (
                                                                        <div style={{ padding: '6px 8px', backgroundColor: '#f0f4ff', borderRadius: '4px', border: '1px solid #dbeafe' }}>
                                                                            <span style={{ ...labelStyle, color: '#2563eb', fontSize: '0.65rem' }}>Filter</span>
                                                                            <div style={{ fontSize: '0.7rem', color: '#1e40af' }}>{sourceInfo.filter}</div>
                                                                        </div>
                                                                    )}

                                                                    {/* Scoring section */}
                                                                    <div style={{ padding: '6px 8px', backgroundColor: '#f0fdf4', borderRadius: '4px', border: '1px solid #bbf7d0' }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                                            <span style={{ ...labelStyle, color: '#16a34a', fontSize: '0.65rem' }}>Scoring Rule</span>
                                                                            <span style={{ fontSize: '0.65rem', color: '#16a34a', fontStyle: 'italic' }}>Default: G≥90, Y≥70</span>
                                                                        </div>
                                                                        <div style={{ fontSize: '0.7rem', color: '#15803d', marginBottom: '8px' }}>{sourceInfo.scoring}</div>
                                                                        
                                                                        {/* Scoring Mode Selection */}
                                                                        <div style={{ marginBottom: '8px' }}>
                                                                            <label style={{ fontSize: '0.65rem', color: '#16a34a', display: 'block', marginBottom: '2px' }}>Mode</label>
                                                                            <select
                                                                                value={override.scoringMode || 'percentage'}
                                                                                onChange={(e) => updateCategoryOverride(cat.id, { scoringMode: e.target.value })}
                                                                                style={{ ...inputStyle, borderColor: '#bbf7d0', color: '#15803d', width: '100%' }}
                                                                            >
                                                                                <option value="percentage">Percentage (Value/Total * 100)</option>
                                                                                <option value="count_min">Count (Min) - Higher is Good</option>
                                                                                <option value="count_max">Count (Max) - Lower is Good</option>
                                                                            </select>
                                                                        </div>

                                                                        {/* Threshold Inputs */}
                                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                                            <div style={{ flex: 1 }}>
                                                                                <label style={{ fontSize: '0.65rem', color: '#16a34a', display: 'block', marginBottom: '2px' }}>
                                                                                    {override.scoringMode === 'count_max' ? 'Green Threshold (≤)' : 'Green Threshold (≥)'}
                                                                                </label>
                                                                                <input 
                                                                                    type="number" 
                                                                                    min="0"
                                                                                    value={override.greenThreshold !== undefined ? override.greenThreshold : 90}
                                                                                    onChange={(e) => updateCategoryOverride(cat.id, { greenThreshold: parseInt(e.target.value) || 0 })}
                                                                                    style={{ ...inputStyle, borderColor: '#bbf7d0', color: '#15803d', width: '100%' }}
                                                                                />
                                                                            </div>
                                                                            <div style={{ flex: 1 }}>
                                                                                <label style={{ fontSize: '0.65rem', color: '#ca8a04', display: 'block', marginBottom: '2px' }}>
                                                                                    {override.scoringMode === 'count_max' ? 'Yellow Threshold (≤)' : 'Yellow Threshold (≥)'}
                                                                                </label>
                                                                                <input 
                                                                                    type="number" 
                                                                                    min="0"
                                                                                    value={override.yellowThreshold !== undefined ? override.yellowThreshold : 70}
                                                                                    onChange={(e) => updateCategoryOverride(cat.id, { yellowThreshold: parseInt(e.target.value) || 0 })}
                                                                                    style={{ ...inputStyle, borderColor: '#fde047', color: '#854d0e', width: '100%' }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {!sourceInfo && selectedSourceId === '' && (
                                                                <div style={{ fontSize: '0.72rem', color: '#6b7280', fontStyle: 'italic', marginTop: '4px' }}>
                                                                    Select which built-in scan analysis logic to apply
                                                                </div>
                                                            )}

                                                            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px dotted #e5e7eb' }}>
                                                                <span style={{ ...labelStyle, fontSize: '0.75rem', color: '#374151' }}>Threshold Note Customization</span>
                                                                
                                                                {(() => {
                                                                    const customNotes = override.customNotes || {};
                                                                    const sourceFinding = combinedDefaultCategories.find(c => c.id === selectedSourceId) || EXTRA_AUTO_SOURCES.find(c => c.id === selectedSourceId);
                                                                    const thresholdVars = [
                                                                        { label: 'Value/Score', value: '{value}' },
                                                                        { label: 'Count', value: '{count}' },
                                                                        { label: 'Total', value: '{total}' },
                                                                        { label: 'Percentage', value: '{percent}' }
                                                                    ];

                                                                    // Resolve category name for threshold lookup
                                                                    const catLabel = selectedSourceId || sourceFinding?.label || cat.label;
                                                                    
                                                                    // Enhanced Threshold Lookup (Exact match -> Case-insensitive -> Trimmed match)
                                                                    const getCatThreshold = () => {
                                                                        if (!dataContext?.category_thresholds) return null;
                                                                        const thresholds = dataContext.category_thresholds;
                                                                        const cleanLabel = catLabel?.trim();
                                                                        const lowerLabel = cleanLabel?.toLowerCase();
                                                                        
                                                                        // 1. Direct hit
                                                                        if (thresholds[cleanLabel]) return thresholds[cleanLabel];
                                                                        
                                                                        // 2. Case-insensitive fuzzy search
                                                                        const match = Object.entries(thresholds).find(([k]) => {
                                                                            const key = k.trim().toLowerCase();
                                                                            return key === lowerLabel || key.includes(lowerLabel) || lowerLabel.includes(key);
                                                                        });
                                                                        
                                                                        return match ? match[1] : null;
                                                                    };
                                                                    
                                                                    const catThreshold = getCatThreshold();
                                                                    const rules = catThreshold?.rules || {};

                                                                    // Map to both 'Healthy' and 'Good' as standard tiers
                                                                    const statusToTier = {
                                                                        'Critical': 'Bad',
                                                                        'Warning': 'Moderate',
                                                                        'Healthy': 'Healthy' 
                                                                    };

                                                                    const updateStatusNote = (status, val) => {
                                                                        updateCategoryOverride(cat.id, { 
                                                                            customNotes: { ...customNotes, [status]: val }
                                                                        });
                                                                    };

                                                                    const TemplateNoteSelectorLocal = ({ status }) => {
                                                                        const tier = statusToTier[status];
                                                                        
                                                                        // Collect all possible tier keys for matching (Healthy vs Good)
                                                                        const tierKeys = tier === 'Healthy' ? ['Healthy', 'Good'] : [tier];
                                                                        
                                                                        let displayNotes = [];
                                                                        const seenTexts = new Set();
                                                                        
                                                                        const addNote = (noteObj, sourceLabel) => {
                                                                            if (!noteObj || !noteObj.text || seenTexts.has(noteObj.text)) return;
                                                                            displayNotes.push({ 
                                                                                ...noteObj, 
                                                                                sourceLabel: sourceLabel + (noteObj.isDefault ? ' Default' : '')
                                                                            });
                                                                            seenTexts.add(noteObj.text);
                                                                        };

                                                                        // Collect notes from all potential tier keys
                                                                        tierKeys.forEach(t => {
                                                                            const tRules = rules[t] || {};
                                                                            const aRules = catThreshold?.default_rules ? catThreshold.default_rules[t] : null;
                                                                            
                                                                            if (Array.isArray(tRules.notes)) tRules.notes.forEach(n => addNote(n, 'Custom'));
                                                                            if (aRules && Array.isArray(aRules.notes)) aRules.notes.forEach(n => addNote(n, 'App Admin'));
                                                                            if (tRules.note) addNote({ text: tRules.note }, 'Custom');
                                                                            if (aRules?.note) addNote({ text: aRules.note }, 'App Admin');
                                                                        });

                                                                        if (displayNotes.length === 0) return null;

                                                                        return (
                                                                            <select
                                                                                className="form-select form-select-sm"
                                                                                style={{ fontSize: '0.7rem', flex: 1, marginBottom: '2px', padding: '2px 4px', height: '24px', cursor: 'pointer', border: '1px solid #0d6efd', color: '#0d6efd', backgroundColor: '#f0f7ff', borderRadius: '4px' }}
                                                                                value=""
                                                                                onChange={(e) => {
                                                                                    const val = e.target.value;
                                                                                    if (val) updateStatusNote(status, val);
                                                                                }}
                                                                            >
                                                                                <option value="">Select Template...</option>
                                                                                {displayNotes.map((n, idx) => (
                                                                                    <option key={idx} value={n.text}>
                                                                                        {n.text.length > 50 ? n.text.substring(0, 47) + '...' : n.text} ({n.sourceLabel})
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        );
                                                                    };

                                                                    const ThresholdVarSelectorLocal = ({ status }) => (
                                                                        <select 
                                                                            className="form-select form-select-sm"
                                                                            style={{ fontSize: '0.7rem', width: 'auto', alignSelf: 'flex-end', marginBottom: '2px', padding: '2px 4px', height: '24px', cursor: 'pointer', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                                                            value=""
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                if (!val) return;
                                                                                const currentVal = customNotes[status] || '';
                                                                                const textareaId = `textarea-${cat.id}-${status}`;
                                                                                const el = document.getElementById(textareaId);
                                                                                let newVal = currentVal;
                                                                                if (el) {
                                                                                    const start = el.selectionStart;
                                                                                    const end = el.selectionEnd;
                                                                                    newVal = currentVal.substring(0, start) + val + currentVal.substring(end);
                                                                                    updateStatusNote(status, newVal);
                                                                                    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(start + val.length, start + val.length); } }, 0);
                                                                                } else {
                                                                                    updateStatusNote(status, currentVal + val);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <option value="">+ Variable</option>
                                                                            {thresholdVars.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                                                                        </select>
                                                                    );

                                                                    return (
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                                                                            {/* Removed redundant category selection - handled by Source Logic above */}
                                                                            {[
                                                                                { label: 'Critical (Red)', status: 'Critical', color: '#dc2626', bgColor: '#fef2f2' },
                                                                                { label: 'Warning (Yellow)', status: 'Warning', color: '#eab308', bgColor: '#fffbeb' },
                                                                                { label: 'Healthy (Green)', status: 'Healthy', color: '#16a34a', bgColor: '#ecfdf5' }
                                                                            ].map(tier => (
                                                                                <div key={tier.status} className="property-field" style={{ marginBottom: '12px' }}>
                                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '8px', marginBottom: '4px' }}>
                                                                                        <label style={{ color: tier.color, fontSize: '0.75rem', fontWeight: '600', marginBottom: 0, whiteSpace: 'nowrap' }}>{tier.label}</label>
                                                                                        <TemplateNoteSelectorLocal status={tier.status} />
                                                                                        <ThresholdVarSelectorLocal status={tier.status} />
                                                                                    </div>
                                                                                    <textarea
                                                                                        id={`textarea-${cat.id}-${tier.status}`}
                                                                                        rows={2}
                                                                                        value={customNotes[tier.status] || ''}
                                                                                        onChange={(e) => updateStatusNote(tier.status, e.target.value)}
                                                                                        placeholder={`Enter custom note for ${tier.status.toLowerCase()} status...`}
                                                                                        style={{ ...inputStyle, width: '100%', fontSize: '0.75rem', padding: '4px', borderRadius: '4px', backgroundColor: tier.bgColor, borderColor: tier.color + '40', resize: 'vertical' }}
                                                                                    />
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            
                            <button
                                onClick={handleAddCategory}
                                style={{
                                    marginTop: '8px', padding: '6px', fontSize: '0.8rem',
                                    border: '1px dashed #d1d5db', borderRadius: '6px',
                                    backgroundColor: '#fff', color: '#6b7280', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                }}
                            >
                                <span>+ Add Grid Item</span>
                            </button>
                        </div>

                        <button
                            onClick={() => updateConfig('dataOverrides', {})}
                            style={{
                                marginTop: '12px', padding: '6px 12px', fontSize: '0.75rem',
                                backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
                                borderRadius: '4px', cursor: 'pointer', color: '#6b7280'
                            }}
                        >
                            Reset All to Auto
                        </button>
                    </div>
                );
            })()}

            {/* Overall Risk Score specific options */}
            {/* {block.type === 'widget' && (block.widgetType?.includes('table') || block.widgetType?.includes('log')) && (
                <div className="property-field">
                    <label>Empty State Message</label>
                    <input
                        type="text"
                        value={block.config.noDataMessage !== undefined ? block.config.noDataMessage : 'No data available'}
                        onChange={(e) => updateConfig('noDataMessage', e.target.value)}
                        placeholder="Message to show when table is empty"
                    />
                </div>
            )}  */}

            {block.type === 'widget' && block.widgetType === 'overall-risk-score' && (
                <div className="property-field">
                    <label>Visible Sections</label>
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '8px', 
                        marginTop: '4px',
                        padding: '10px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                    }}>
                        {[
                            { key: 'showRatingKey', label: 'Rating Key' },
                            { key: 'showRiskStatus', label: 'Risk Status Message' },
                            { key: 'showBusinessProtection', label: 'Business Protection Text' },
                            { key: 'showChart', label: 'Chart' },
                        ].map(section => {
                            const isChecked = block.config?.[section.key] !== false; // Default to true
                            
                            return (
                                <label key={section.key} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px', 
                                    cursor: 'pointer', 
                                    fontWeight: 'normal',
                                    fontSize: '0.8rem',
                                    color: '#374151',
                                    marginBottom: 0
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => updateConfig(section.key, e.target.checked)}
                                    />
                                    {section.label}
                                </label>
                            );
                        })}
                    </div>

                    {/* Chart Type Selection */}
                    {(block.config?.showChart !== false) && (
                        <div style={{ marginTop: '12px' }}>
                            <label style={{ fontSize: '0.8rem', marginBottom: '4px', display: 'block' }}>Chart Type</label>
                            <select
                                value={block.config?.chartType || 'doughnut'}
                                onChange={(e) => updateConfig('chartType', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '6px',
                                    fontSize: '0.8rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px'
                                }}
                            >
                                <option value="doughnut">Doughnut Chart</option>
                                <option value="pie">Pie Chart</option>
                            </select>
                        </div>
                    )}
                </div>
            )}

            {/* ELEMENTS: Type-specific content fields */}
            {block.type === 'element' && (
                <>
                    {/* Element Dependency Configuration */}
                    {!block.isPlaceholderWidget && (
                        <div className="property-field">
                            <label>Parent Widget (Dependency)</label>
                            <select
                                value={block.config.dependentBlockId || ''}
                                onChange={(e) => updateConfig('dependentBlockId', e.target.value)}
                                className="form-select"
                            >
                                <option value="">None (Always Visible)</option>
                                {availableParentWidgets.map(w => (
                                    <option key={w.id} value={w.id}>
                                        {w.label}
                                    </option>
                                ))}
                            </select>
                            <small style={{ display: 'block', marginTop: '4px', color: '#6c757d', fontSize: '0.75rem' }}>
                                Hidden if parent widget has no data
                            </small>
                        </div>
                    )}
                    <hr style={{ margin: '15px 0', borderColor: '#dee2e6' }} />

                    {/* Title Element */}
                    {block.elementType === 'title' && (
                        <>
                            <div className="property-field">
                                <label>Title</label>
                                {renderVariableSelect('content', block.config.content)}
                                <input
                                    id={`input-${block.id}-content`}
                                    type="text"
                                    value={block.config.content || ''}
                                    onChange={(e) => updateConfig('content', e.target.value)}
                                    placeholder="Title Text"
                                />
                            </div>
                        </>
                    )}

                    {/* Text Element */}
                    {block.elementType === 'text' && (
                        <div className="property-field">
                            <label>Content</label>
                            {renderVariableSelect('content', block.config.content)}
                            <textarea
                                id={`input-${block.id}-content`}
                                value={block.config.content || ''}
                                onChange={(e) => updateConfig('content', e.target.value)}
                                rows={5}
                                placeholder="Enter text content..."
                            />
                        </div>
                    )}

                    {/* Image Element */}
                    {block.elementType === 'image' && (
                        <>
                            <div className="property-field">
                                <label>Image Source</label>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                    {renderVariableSelect('src', block.config.src)}
                                    <input
                                        type="text"
                                        value={block.config.src || ''}
                                        onChange={(e) => updateConfig('src', e.target.value)}
                                        placeholder="Image URL"
                                        style={{ flex: 1 }}
                                    />
                                </div>
                            </div>
                            <div className="property-field">
                                <label>Alt Text</label>
                                <input
                                    type="text"
                                    value={block.config.alt || ''}
                                    onChange={(e) => updateConfig('alt', e.target.value)}
                                    placeholder="Description of image"
                                />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div className="property-field">
                                    <label>Horizontal Alignment</label>
                                    <select
                                        value={block.config.align || 'center'}
                                        onChange={(e) => updateConfig('align', e.target.value)}
                                    >
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                    </select>
                                </div>
                                <div className="property-field">
                                    <label>Vertical Alignment</label>
                                    <select
                                        value={block.config.verticalAlign || 'middle'}
                                        onChange={(e) => updateConfig('verticalAlign', e.target.value)}
                                    >
                                        <option value="top">Top</option>
                                        <option value="middle">Middle</option>
                                        <option value="bottom">Bottom</option>
                                    </select>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div className="property-field">
                                    <label>Image Width (e.g. 100% or 200px)</label>
                                    <input
                                        type="text"
                                        value={block.config.imageWidth || '100%'}
                                        onChange={(e) => updateConfig('imageWidth', e.target.value)}
                                        placeholder="100%"
                                    />
                                </div>
                                <div className="property-field">
                                    <label>Image Height (e.g. 100% or 200px)</label>
                                    <input
                                        type="text"
                                        value={block.config.imageHeight || '100%'}
                                        onChange={(e) => updateConfig('imageHeight', e.target.value)}
                                        placeholder="100%"
                                    />
                                </div>
                            </div>
                            <div className="property-field">
                                <label>Object Fit</label>
                                <select
                                    value={block.config.objectFit || 'contain'}
                                    onChange={(e) => updateConfig('objectFit', e.target.value)}
                                >
                                    <option value="contain">Contain (Fit inside)</option>
                                    <option value="cover">Cover (Fill & Crop)</option>
                                    <option value="fill">Fill (Stretch)</option>
                                    <option value="scale-down">Scale Down</option>
                                    <option value="none">None (Original Size)</option>
                                </select>
                                <small className="help-text" style={{ display: 'block', marginTop: '4px', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                                    Controls how the image resizes to fill its dimensions.
                                </small>
                            </div>
                        </>
                    )}

                    {/* Shape Element */}
                    {block.elementType === 'shape' && (
                        <>
                            <div className="property-field">
                                <label>Shape Type</label>
                                <select
                                    value={block.config.shape || 'rounded'}
                                    onChange={(e) => updateConfig('shape', e.target.value)}
                                >
                                    <option value="square">Square / Rectangle</option>
                                    <option value="circle">Circle / Oval</option>
                                    <option value="rounded">Rounded Square</option>
                                </select>
                            </div>
                            
                            <div className="property-field" style={{ marginTop: '15px', borderTop: '1px solid #e5e7eb', paddingTop: '15px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                                    <span style={{ fontSize: '14px' }}>📊</span> Status Color Sync
                                </label>
                                <small style={{ display: 'block', color: '#666', marginBottom: '8px' }}>
                                    Automatically set shape color based on a findings category status.
                                </small>
                                <select
                                    value={block.config.statusSyncCategory || ''}
                                    onChange={(e) => updateConfig('statusSyncCategory', e.target.value)}
                                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                >
                                    <option value="">Disabled (Manual Color)</option>
                                    <optgroup label="Findings Categories">
                                        {MANDATORY_CATEGORIES.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </optgroup>
                                </select>
                                {block.config.statusSyncCategory && (
                                    <small style={{ display: 'block', marginTop: '4px', color: '#2563eb', fontStyle: 'italic' }}>
                                        Note: Manual background color will be overridden while sync is active.
                                    </small>
                                )}
                            </div>
                        </>
                    )}

                    {/* Icon Element */}
                    {block.elementType === 'icon' && (
                        <div className="property-field">
                            <label>Icon Type</label>
                            <select
                                value={block.config.icon || 'star'}
                                onChange={(e) => updateConfig('icon', e.target.value)}
                            >
                                <option value="star">Star</option>
                                <option value="heart">Heart</option>
                                <option value="alert">Alert</option>
                                <option value="check">Check</option>
                                <option value="info">Info</option>
                                <option value="user">User</option>
                                <option value="settings">Settings</option>
                                <option value="menu">Menu</option>
                                <option value="search">Search</option>
                            </select>
                        </div>
                    )}

                    {/* Header/Footer Elements */}
                    {(block.elementType === 'header' || block.elementType === 'footer') && (
                        <div className="property-field">
                            <label>Content</label>
                            {renderVariableSelect('content', block.config.content)}
                            <input
                                id={`input-${block.id}-content`}
                                type="text"
                                value={block.config.content || ''}
                                onChange={(e) => updateConfig('content', e.target.value)}
                            />
                            <small>Global {block.elementType} content (appears on designated pages)</small>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ContentTab;
